import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { gantt } from 'dhtmlx-gantt';
import 'dhtmlx-gantt/codebase/dhtmlxgantt.css';

// Import utility functions (keeping only data utils for now)
import { 
  parseNum
} from '../utils/ganttDataUtils';

// Import hooks
import { useGanttFlat } from '../hooks/useGanttFlat';
import { useInstallationTeams } from '../hooks/useInstallationTeams';

// Import services
import * as ganttService from '../../../../services/gantt';
import ActualProgressDialog from './ActualProgressDialog';
import { useProgressSubmissions } from '../hooks/useProgressSubmissions';

// Import components
import AddTaskDialog from './AddTaskDialog';
import TaskSplitDialog from './TaskSplitDialog';

// Configuration flags
const ENABLE_QUANTITY_PROGRESS_UPDATE = false; // Set to true to enable quantity/progress updates in popup

// Map flat API to dhtmlx-gantt format
function mapToDhx(raw, teams) {
  const teamMap = new Map();
  (teams || []).forEach((t) => {
    const key = String(t.id ?? '');
    const name = t.name ?? '';
    if (key) teamMap.set(key, name);
  });

  const tasks = [];
  const links = [];
  const isFolder = (n) => n.isFolder || n.type === 'project' || n.type === 'modularUnit' || n.type === 'workCategory';
  // Build map for quick lookup
  const byId = new Map();
  (raw || []).forEach((n) => byId.set(String(n.id), n));

  // Create tasks
  (raw || []).forEach((n) => {
    const id = String(n.id);
    const text = n.text ?? n.name ?? `Task ${id}`;
    const parent = n.parent && n.parent !== 0 ? String(n.parent) : 0;

    if (isFolder(n)) {
      // Preserve original type in nodeType for coloring
      const nodeType = n.type === 'project' ? 'project' : (n.type === 'modularUnit' ? 'modularUnit' : 'workCategory');
      tasks.push({ id, text, type: gantt.config.types.project, nodeType, parent, open: n.open !== false });
      return;
    }

    // task
    const start_raw = n.start_date ?? n.startDate;
    const end_raw = n.end_date ?? n.endDate;
    // actual dates (from backend tasks table)
    const actual_start_raw = n.actualStartDate ?? n.actual_start_date ?? n.actual_startdate;
    const actual_end_raw = n.actualEndDate ?? n.actual_end_date ?? n.actual_enddate;
    // Normalize times so bars cover full days (start at 00:00:01, end at 23:59:59)
    const start_date = start_raw ? `${String(start_raw).slice(0,10)} 00:00:01` : null;
    const end_date = end_raw ? `${String(end_raw).slice(0,10)} 23:59:59` : null;
    // dhtmlx expects start_date as string with known format, and duration in days
    // We'll use ISO date strings and set gantt.config.date_format accordingly.
    const duration = parseNum(n.duration ?? n.days ?? 1, 1);
    let progress = parseNum(n.progress ?? 0, 0);
    if (progress <= 1) progress = Math.round(progress * 100) / 100; // keep as 0..1 for dhtmlx but convert if 0..1 given

    const assigned = n.subcontractorName
      ? n.subcontractorName
      : (n.assignedTo != null
          ? (teamMap.get(String(n.assignedTo)) || `Team ID: ${n.assignedTo}`)
          : '');

    // Normalize quantity fields from backend (support multiple aliases)
    const qtyRaw = (n.quantity != null ? n.quantity
                  : (n.totalQuantity != null ? n.totalQuantity
                  : (n.qty != null ? n.qty
                  : (n.total_qty != null ? n.total_qty : null))));
    const compQtyRaw = (n.completedQuantity != null ? n.completedQuantity
                      : (n.completed_qty != null ? n.completed_qty
                      : (n.actualQuantity != null ? n.actualQuantity : null)));
    const qtyNum = parseNum(qtyRaw, null);
    const compQtyNum = parseNum(compQtyRaw, 0);

    tasks.push({
      id,
      text,
      parent,
      start_date,
      end_date,
      duration,
      progress,
      assigned,
      assignedTo: n.assignedTo ?? null,
      weight: n.weight,
      quantity: Number.isFinite(qtyNum) ? qtyNum : null,
      completedQuantity: Number.isFinite(compQtyNum) ? compQtyNum : 0,
      unit: n.unit,
      nodeType: 'task',
      open: true,
      // preserve actual dates on task for custom actual layer
      actualStartDate: actual_start_raw ? `${String(actual_start_raw).slice(0,10)} 00:00:01` : null,
      actualEndDate: actual_end_raw ? `${String(actual_end_raw).slice(0,10)} 23:59:59` : null,
    });

    

    // Clear time-section observers/pollers on cancel
    gantt.attachEvent('onLightboxCancel', function(id){
      try {
        if (window.__g_time_poll) { clearInterval(window.__g_time_poll); window.__g_time_poll = null; }
        const cont = window.__g_time_container;
        if (cont && cont.__mo) { try { cont.__mo.disconnect(); } catch(_) {} }
        window.__g_time_container = null;
      } catch(_) {}
      return true;
    });
  });

  // Links (dependencies) — sanitize cycles and unknown ids
  const idSet = new Set(tasks.map((t) => String(t.id)));
  const adj = new Map();
  tasks.forEach((t) => adj.set(String(t.id), new Set()));
  const hasCycleIfAdd = (from, to) => {
    const seen = new Set();
    const stack = [to];
    while (stack.length) {
      const cur = stack.pop();
      if (cur === from) return true;
      if (seen.has(cur)) continue;
      seen.add(cur);
      (adj.get(cur) || []).forEach((n) => stack.push(n));
    }
    return false;
  };

  (raw || []).forEach((n) => {
    if (!Array.isArray(n.dependencies) || isFolder(n)) return;
    const successor = String(n.id);
    n.dependencies.forEach((d, idx) => {
      const predecessor = String(d.taskId ?? d.id ?? '');
      if (!predecessor || !idSet.has(predecessor) || predecessor === successor) return;
      if (hasCycleIfAdd(predecessor, successor)) return;
      adj.get(predecessor).add(successor);
      let type = '0'; // FS
      const t = String(d.type || '').toUpperCase();
      if (t === 'SS') type = '1';
      else if (t === 'FF') type = '2';
      else if (t === 'SF') type = '3';
      links.push({ id: `${predecessor}_${successor}_${idx}`, source: predecessor, target: successor, type });
    });
  });

  return { data: tasks, links };
}

export default function GanttIntegrated({ onReady, searchText, unitFilterId, showActual }) {
  const { raw, refresh } = useGanttFlat();
  const { teams } = useInstallationTeams();
  const ganttRef = useRef(null);
  const [height, setHeight] = useState(520);
  
  const lastLightboxTaskId = useRef(null);
  const { projectId } = useParams();
  const { loadingIndex: progressIndexLoading, hasApprovedForTask, getItemsByTask, getWorkDatesForTask, getTotalVolumeForTask } = useProgressSubmissions(projectId);
  const openActualDialogRef = useRef(null);
  // Actual progress dialog state
  const [actualDialogOpen, setActualDialogOpen] = useState(false);
  const [actualDialogLoading, setActualDialogLoading] = useState(false);
  const [actualDialogTask, setActualDialogTask] = useState(null);
  const [actualDialogItems, setActualDialogItems] = useState([]);
  
  // Add Task Dialog state
  const [addTaskDialogOpen, setAddTaskDialogOpen] = useState(false);
  const [selectedCategoryForNewTask, setSelectedCategoryForNewTask] = useState(null);
  const [addTaskLoading, setAddTaskLoading] = useState(false);
  // Split Task Dialog state
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [taskToSplit, setTaskToSplit] = useState(null);
  
  // Handle opening add task dialog
  const handleOpenAddTaskDialog = useCallback(() => {
    const sel = gantt.getSelectedId();
    if (!sel) {
      gantt.message({ type: 'warning', text: 'Pilih baris terlebih dahulu' });
      return;
    }
    
    // Resolve workCategory from selected row
    const resolveWCFromSelection = (id) => {
      try {
        const cur = gantt.getTask(id);
        if (!cur) return null;
        // Helper: get nearest ancestor modular unit name for header
        // NOTE: This walks up the parent chain to find a node with nodeType/type 'modularUnit'
        const getUnitName = (nodeObj) => {
          try {
            let p = nodeObj;
            // If given an id, resolve once
            if (p && typeof p !== 'object') {
              p = gantt.getTask(p);
            }
            while (p) {
              if (p.nodeType === 'modularUnit' || p.type === 'modularUnit') {
                return p.text || 'Unit';
              }
              const parentId = p.parent;
              if (!parentId) break;
              p = gantt.getTask(parentId);
            }
          } catch (_) {}
          return 'Unit';
        };
        
        // If selected row is a workCategory, use its identifier
        if (cur.nodeType === 'workCategory' || cur.type === 'workCategory') {
          const directWC = cur.workCategoryId ?? cur.work_category_id ?? cur.work_categoryId;
          if (directWC != null && directWC !== '' && isFinite(Number(directWC))) {
            return { id: String(Number(directWC)), name: cur.text || 'Kategori', unitName: getUnitName(cur) };
          }
          const m = String(cur.id).match(/\d+/);
          if (m) {
            return { id: m[0], name: cur.text || 'Kategori', unitName: getUnitName(cur) };
          }
        }
        
        // If selected row is a task, get its workCategory
        const direct = cur.workCategoryId ?? cur.work_category_id ?? cur.work_categoryId;
        if (direct != null && direct !== '' && isFinite(Number(direct))) {
          // Find the category name from parent
          let p = cur.parent;
          while (p) {
            const pt = gantt.getTask(p);
            if (!pt) break;
            if (pt.nodeType === 'workCategory' || pt.type === 'workCategory') {
              return { id: String(Number(direct)), name: pt.text || 'Kategori', unitName: getUnitName(pt) };
            }
            p = pt.parent;
          }
          return { id: String(Number(direct)), name: 'Kategori', unitName: getUnitName(cur) };
        }
        
        // Find nearest ancestor of type workCategory
        let p = cur.parent;
        while (p) {
          const pt = gantt.getTask(p);
          if (!pt) break;
          if (pt.nodeType === 'workCategory' || pt.type === 'workCategory') {
            const pDirect = pt.workCategoryId ?? pt.work_category_id ?? pt.work_categoryId;
            if (pDirect != null && pDirect !== '' && isFinite(Number(pDirect))) {
              return { id: String(Number(pDirect)), name: pt.text || 'Kategori', unitName: getUnitName(pt) };
            }
            const m2 = String(pt.id).match(/\d+/);
            if (m2) {
              return { id: m2[0], name: pt.text || 'Kategori', unitName: getUnitName(pt) };
            }
          }
          p = pt.parent;
        }
        return null;
      } catch(_) { 
        return null; 
      }
    };
    
    const categoryData = resolveWCFromSelection(sel);
    if (!categoryData) {
      gantt.message({ type: 'warning', text: 'Pilih kategori pekerjaan terlebih dahulu' });
      return;
    }
    
    setSelectedCategoryForNewTask(categoryData);
    setAddTaskDialogOpen(true);
  }, []);
  
  // Handle saving new task from dialog
  const handleSaveNewTask = useCallback(async (taskData) => {
    if (!selectedCategoryForNewTask) {
      gantt.message({ type: 'error', text: 'Data kategori tidak ditemukan' });
      return;
    }
    
    setAddTaskLoading(true);
    try {
      const wcNum = parseInt(selectedCategoryForNewTask.id, 10);
      if (!Number.isFinite(wcNum)) {
        gantt.message({ type: 'warning', text: 'Kategori pekerjaan tidak valid' });
        return;
      }
      
      const payload = {
        ...taskData,
        workCategoryId: wcNum,
      };
      
      gantt.message({ type: 'info', text: 'Membuat tugas...' });
      const res = await ganttService.createTask(String(wcNum), payload);
      const data = res?.data ?? res?.task ?? res;
      const newId = String(data?.id ?? data?.taskId ?? data?._id ?? '');
      
      if (!newId) {
        gantt.message({ type: 'error', text: 'Gagal mengambil ID tugas baru' });
        console.error('CreateTask response missing id', res);
        return;
      }
      
      gantt.message({ type: 'success', text: 'Tugas berhasil dibuat' });
      setAddTaskDialogOpen(false);
      setSelectedCategoryForNewTask(null);
      
      // Refresh data to show new task
      setTimeout(() => {
        try { 
          refresh(); 
        } catch (_) {}
      }, 300);
      
    } catch (e) {
      gantt.message({ type: 'error', text: 'Gagal membuat tugas' });
      console.error('Create task failed (dialog)', e);
    } finally {
      setAddTaskLoading(false);
    }
  }, [selectedCategoryForNewTask, refresh]);
  
  // Handle closing add task dialog
  const handleCloseAddTaskDialog = useCallback(() => {
    if (!addTaskLoading) {
      setAddTaskDialogOpen(false);
      setSelectedCategoryForNewTask(null);
    }
  }, [addTaskLoading]);

  // Split dialog handlers
  const handleCloseSplitDialog = useCallback(() => {
    setSplitDialogOpen(false);
    setTaskToSplit(null);
  }, []);

  const handleTaskSplit = useCallback(async (splitData) => {
    try {
      const { originalTask, splitDate, resumeDate, task2EndDate, task1Progress, task2Progress, task1Quantity, task2Quantity, task1Name, task2Name, namingFormat, reason } = splitData;
      if (!projectId) throw new Error('Missing project ID');
      if (!originalTask?.id) throw new Error('Missing task ID');
      // Blur active element before showing a message to avoid aria-hidden warning
      if (document.activeElement) {
        document.activeElement.blur();
      }

      // Helper to format as YYYY-MM-DD
      const fmt = (d) => {
        const x = d instanceof Date ? d : new Date(d);
        if (isNaN(x.getTime())) return null;
        const y = x.getFullYear();
        const m = String(x.getMonth() + 1).padStart(2, '0');
        const day = String(x.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      // Call tidy service API (mirrors createTask style)
      const res = await ganttService.splitTask(projectId, originalTask.id, {
        splitDate: fmt(splitDate),
        resumeDate: fmt(resumeDate),
        task2EndDate: fmt(task2EndDate),
        task1Progress, // already decimal
        task2Progress, // already decimal
        task1Quantity,
        task2Quantity,
        task1Name,
        task2Name,
        namingFormat,
        reason,
      });

      if (res?.success) {
        // Blur active element before showing a message to avoid aria-hidden warning
        if (document.activeElement) {
          document.activeElement.blur();
        }
        try { gantt.message({ type: 'success', text: 'Tugas berhasil di-split' }); } catch(_) {}
        handleCloseSplitDialog();
        setTimeout(() => { try { refresh(); } catch(_) {} }, 300);
      } else {
        throw new Error(res?.message || 'Split task failed');
      }
    } catch (err) {
      console.error('Error splitting task:', err);
      // Blur active element before showing a message to avoid aria-hidden warning
      if (document.activeElement) {
        document.activeElement.blur();
      }
      try { gantt.message({ type: 'error', text: 'Gagal split task' }); } catch(_) {}
    }
  }, [projectId, refresh, handleCloseSplitDialog]);
  // Track tasks created via our Add flow that should POST on save
  const pendingNewTasksRef = useRef(new Set()); // stores String IDs
  // Remember the resolved workCategoryId for each pending-new temp id
  const pendingNewTaskWCRef = useRef(new Map()); // key: String(id) => Number(workCategoryId)

  // Timeline drag scrolling functionality
  const initTimelineDrag = useCallback(() => {
    // Find the correct scrollable container in dhtmlx-gantt
    const ganttContainer = ganttRef.current;
    if (!ganttContainer) return;
    
    // Try multiple selectors to find the scrollable timeline area
    const timelineArea = ganttContainer.querySelector('.gantt_task_area') || 
                        ganttContainer.querySelector('.gantt_timeline_area') || 
                        ganttContainer.querySelector('.gantt_data_area') ||
                        ganttContainer.querySelector('.gantt_task');
    
    if (!timelineArea) {
      console.warn('Timeline area not found for drag scrolling');
      return;
    }
    
    console.log('Timeline drag initialized on:', timelineArea.className);
    
    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;
    
    const handleMouseDown = (e) => {
      // Only enable drag on timeline background, not on tasks or links
      if (e.target.closest('.gantt_task_line, .gantt_link_line, .gantt_task_link, .gantt_bar_task')) return;
      
      isDragging = true;
      startX = e.clientX;
      
      // Get current scroll position using Gantt API
      startScrollLeft = gantt.getScrollState().x || 0;
      
      timelineArea.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none'; // Prevent text selection
      e.preventDefault();
      
      console.log('Drag started at X:', startX, 'Scroll:', startScrollLeft);
    };
    
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      
      const currentX = e.clientX;
      const deltaX = startX - currentX; // Reversed for natural scrolling
      const newScrollLeft = startScrollLeft + deltaX;
      
      // Use Gantt API to scroll
      try {
        gantt.scrollTo(newScrollLeft, null);
      } catch (err) {
        // Fallback to direct DOM scrolling
        if (timelineArea.scrollLeft !== undefined) {
          timelineArea.scrollLeft = newScrollLeft;
        }
      }
    };
    
    const handleMouseUp = () => {
      if (!isDragging) return;
      
      isDragging = false;
      timelineArea.style.cursor = 'grab';
      document.body.style.userSelect = ''; // Restore text selection
      
      console.log('Drag ended');
    };
    
    const handleMouseLeave = () => {
      if (isDragging) {
        isDragging = false;
        timelineArea.style.cursor = 'grab';
        document.body.style.userSelect = '';
      }
    };
    
    // Add event listeners
    timelineArea.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    timelineArea.addEventListener('mouseleave', handleMouseLeave);
    
    // Store cleanup function
    timelineArea._dragCleanup = () => {
      timelineArea.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      timelineArea.removeEventListener('mouseleave', handleMouseLeave);
      document.body.style.userSelect = '';
    };
    
    console.log('Timeline drag scrolling initialized successfully');
  }, []);

  // Preload dom-to-image for PNG fallback export
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.domtoimage) return;
    const id = 'dom-to-image-more-cdn';
    if (document.getElementById(id)) return;
    const s = document.createElement('script');
    s.id = id;
    s.async = true;
    s.src = 'https://cdn.jsdelivr.net/npm/dom-to-image-more@3.3.6/dist/dom-to-image-more.min.js';
    document.body.appendChild(s);
  }, []);

  // Preload html2canvas as secondary fallback
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.html2canvas) return;
    const id = 'html2canvas-cdn';
    if (document.getElementById(id)) return;
    const s = document.createElement('script');
    s.id = id;
    s.async = true;
    s.src = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js';
    document.body.appendChild(s);
  }, []);

  

  // Ensure Select2 assets are present (loads from CDN if missing)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hasSelect2 = () => !!(window.$ && window.$.fn && window.$.fn.select2);
    if (hasSelect2()) { return; }

    const addStyle = (href, id) => {
      if (document.getElementById(id)) return;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.id = id;
      document.head.appendChild(link);
    };
    const addScript = (src, id, onload) => {
      if (document.getElementById(id)) { if (onload) onload(); return; }
      const s = document.createElement('script');
      s.src = src;
      s.id = id;
      s.async = true;
      if (onload) s.onload = onload;
      document.body.appendChild(s);
    };

    // Load jQuery if missing, then Select2
    const ensureSelect2 = () => {
      if (hasSelect2()) { return; }
      addStyle('https://cdn.jsdelivr.net/npm/select2@4.1.0-rc.0/dist/css/select2.min.css', 'select2-css');
      addScript('https://cdn.jsdelivr.net/npm/select2@4.1.0-rc.0/dist/js/select2.min.js', 'select2-js', null);
    };

    if (!(window.$ || window.jQuery)) {
      addScript('https://cdn.jsdelivr.net/npm/jquery@3.6.4/dist/jquery.min.js', 'jquery-js', ensureSelect2);
    } else {
      ensureSelect2();
    }
  }, []);

  const dataset = useMemo(() => mapToDhx(raw, teams), [raw, teams]);

  useEffect(() => {
    const containerEl = ganttRef.current;
    // Store raw data globally for split access
    window.__ganttFlatRaw = raw;
    
    // Configure dhtmlx gantt once
    gantt.config.date_format = '%Y-%m-%d %H:%i:%s'; // match normalized timestamps
    gantt.config.readonly = false;
    gantt.config.details_on_dblclick = true;
    // Disable interactive progress change per requirements
    gantt.config.drag_progress = false;
    
    // Enable multiple links and interactive link creation
    gantt.config.drag_links = true; // Allow creating links by dragging
    gantt.config.show_links = true; // Show dependency links
    gantt.config.auto_scheduling = false; // Disable auto-scheduling to allow manual link management
    gantt.config.auto_scheduling_strict = false;
    
    // Localize duration unit label
    gantt.locale.labels.days = 'Hari';
    gantt.locale.labels.day = 'Hari';
    // Center lightbox and custom title
    gantt.templates.lightbox_header = function(start, end, task) {
      return 'Detail Data Pekerjaan';
    };
    // Localize lightbox buttons and add Split
    try {
      gantt.locale.labels.gantt_save_btn = 'Ubah Data';
      gantt.locale.labels.gantt_cancel_btn = 'Batalkan';
      gantt.locale.labels.gantt_delete_btn = 'Hapus';
      gantt.locale.labels.gantt_split_btn = 'Split Task';
      gantt.config.buttons_left = ['gantt_save_btn', 'gantt_cancel_btn'];
      gantt.config.buttons_right = ['gantt_delete_btn', 'gantt_split_btn'];
      console.log('[DEBUG] Gantt button config set:', gantt.config.buttons_left, gantt.config.buttons_right);
    } catch(_) {}
    // Let gantt adapt its inner areas vertically (no inner scrollbars)
    gantt.config.autosize = 'y';
    // Ensure native progress element is rendered (we style it via CSS)
    gantt.config.show_progress = true;

    // Enable resizer; we'll set exact split after init
    gantt.config.grid_resizer = false;
    // Make list rows taller for readability
    gantt.config.row_height = 36;
    gantt.config.bar_height = 22;
    gantt.config.scale_height = 64; // taller scale for readability
    // Enable drag-and-drop row ordering within and across branches
    gantt.config.order_branch = true;
    gantt.config.order_branch_free = true;

    // Scales: Month > Week > Day
    gantt.config.scales = [
      { unit: 'month', step: 1, format: '%F, %Y' },
      { unit: 'week', step: 1, format: (date) => {
        const weekStart = gantt.date.week_start(new Date(date));
        const weekEnd = gantt.date.add(new Date(weekStart), 6, 'day');
        const opts = { month: 'short', day: '2-digit' };
        return `Minggu #${gantt.date.getISOWeek(date)}\n${weekStart.toLocaleDateString('id-ID', opts)} - ${weekEnd.toLocaleDateString('id-ID', opts)}`;
      }},
      { unit: 'day', step: 1, format: '%j %M' },
    ];

    // Columns (removed Actions column)
    gantt.config.columns = [
      { name: 'text', label: 'Nama', tree: true, width: 300 },
      { name: 'start_date', label: 'Mulai', width: 100, align: 'center' },
      { name: 'end_date', label: 'Selesai', width: 100, align: 'center' },
      { name: 'duration', label: 'Durasi (hari)', width: 120, align: 'center' },
      // Display task weight right after duration
      { name: 'weight', label: 'Bobot (%)', width: 110, align: 'center', template: (task) => {
        // Render weight with 3 decimals followed by %; empty for non-task rows or missing values
        const w = Number(task.weight);
        return Number.isFinite(w) ? `${w.toFixed(3)}%` : '';
      } },
      // Display Volume (quantity + unit) after weight
      { name: 'volume', label: 'Volume', width: 140, align: 'center', template: (task) => {
        // For Actual row: show total actual volume; else show task planned quantity
        const unit = task.unit ? String(task.unit) : '';
        if (task.nodeType === 'actual') {
          const av = Number(task.actualTotalVolume);
          if (!Number.isFinite(av)) return '';
          return unit ? `${av.toFixed(2)} ${unit}` : av.toFixed(2);
        }
        const q = Number(task.quantity);
        if (!Number.isFinite(q)) return '';
        return unit ? `${q.toFixed(2)} ${unit}` : q.toFixed(2);
      } },
      { name: 'progress', label: 'Progress', width: 100, align: 'center', template: (task) => {
        const p = Number(task.progress ?? 0);
        const pct = p <= 1 ? Math.round(p * 100) : Math.round(p);
        let color = '#fb8c00';
        if (pct >= 100) color = '#2e7d32';
        else if (pct >= 51) color = '#1e88e5';
        return `<span style="color:${color}; font-weight:600">${pct}%</span>`;
      } },
      { name: 'pred', label: 'Predecessors', width: 150, align: 'center', template: (task) => {
        if (!task.pred) return '';
        // Handle multiple predecessors separated by comma
        const predParts = String(task.pred).split(',').filter(p => p.trim());
        if (predParts.length <= 1) {
          return `<span style="color:#1976d2">${task.pred}</span>`;
        }
        // Show count for multiple predecessors
        const firstPred = predParts[0].trim();
        const count = predParts.length;
        return `<span style="color:#1976d2" title="${task.pred}">${firstPred} +${count-1}</span>`;
      }},
    ];

    gantt.templates.task_text = (start, end, task) => task.text; // title on top
    // Remove native progress text overlay
    gantt.templates.progress_text = function() { return ''; };

    // Color bars by nodeType and add progress class for coloring the progress strip
    gantt.templates.task_class = (start, end, task) => {
      let base = '';
      switch (task.nodeType) {
        case 'project': base = 'bar-project'; break;
        case 'modularUnit': base = 'bar-modular'; break;
        case 'workCategory': base = 'bar-category'; break;
        case 'task': base = 'bar-task'; break;
        case 'actual': base = 'bar-actual'; break;
        default: base = ''; break;
      }
      const p = Number(task?.progress || 0);
      const pct = p <= 1 ? Math.round(p * 100) : Math.round(p);
      let progClass = 'prog-low';
      if (pct >= 100) progClass = 'prog-high';
      else if (pct >= 51) progClass = 'prog-mid';
      return [base, progClass].filter(Boolean).join(' ');
    };

    // Helper: strip trailing volume/unit suffix appended to task name, e.g. "(37.43 m3)"
    const stripVolumeSuffix = (txt) => {
      if (!txt) return txt;
      let prev;
      let out = String(txt);
      const re = /\s*\(\s*\d+[.,]?\d*\s*[a-zA-Z]+\s*\)\s*$/;
      do { prev = out; out = out.replace(re, ''); } while (out !== prev);
      return out.trim();
    };

    // Allow editing only for real tasks and track current lightbox task id
    gantt.attachEvent('onBeforeLightbox', function(id) {
      try { lastLightboxTaskId.current = id; } catch(_) {}
      const t = gantt.getTask(id);
      if (t?.nodeType === 'actual') return false; // block lightbox for Actual row
      try { if (t && t.text) { t.text = stripVolumeSuffix(t.text); } } catch(_) {}
      return t?.nodeType === 'task';
    });

    // Block drag/resize for Actual rows
    gantt.attachEvent('onBeforeTaskDrag', function(id, mode, e){
      try { const t = gantt.getTask(id); if (t?.nodeType === 'actual') return false; } catch(_) {}
      return true;
    });

    // Block link creation if source/target is an Actual row
    gantt.attachEvent('onBeforeLinkAdd', function(id, link){
      try {
        const s = gantt.getTask(link.source);
        const t = gantt.getTask(link.target);
        if (s?.nodeType === 'actual' || t?.nodeType === 'actual') return false;
      } catch(_) {}
      return true;
    });

    // Double click: ONLY actual row opens the dialog. Tasks keep default (open lightbox).
    gantt.attachEvent('onTaskDblClick', function(id, e){
      try {
        const t = gantt.getTask(id);
        if (t?.nodeType === 'actual') {
          const parent = t.parent ? gantt.getTask(t.parent) : null;
          if (parent && parent.nodeType === 'task') { if (openActualDialogRef.current) { openActualDialogRef.current(parent); } return false; }
          return false;
        }
      } catch(_) {}
      // let dhtmlx handle normal tasks (open lightbox)
      return true;
    });

    // Disable legacy in-bar overlay for Actual progress to avoid conflicting visuals.
    gantt.templates.task_content = function() { return ''; };

    // Helpers used by interactive updates
    const fmtYMD = gantt.date.date_to_str('%Y-%m-%d');
    const toYMD = (v) => {
      if (!v) return null;
      if (v instanceof Date) return fmtYMD(v);
      if (typeof v === 'string') return v.slice(0, 10);
      const d = new Date(v);
      return isNaN(d.getTime()) ? null : fmtYMD(d);
    };
    const atStartOfDay = (d) => {
      const x = new Date(d);
      x.setHours(0,0,1,0); // 00:00:01
      return x;
    };
    const atEndOfDay = (d) => {
      const x = new Date(d);
      x.setHours(23,59,59,0); // 23:59:59
      return x;
    };
    const normalizeTaskFullDay = (tt) => {
      if (!tt) return;
      const dayMs = 24*60*60*1000;
      // Ensure start normalized
      if (tt.start_date) tt.start_date = atStartOfDay(tt.start_date);
      // Prefer deriving end from start + (duration-1) to avoid time component drift
      if (tt.start_date && (tt.duration != null)) {
        const dur = Math.max(1, Number(tt.duration) || 1);
        const s0 = new Date(tt.start_date);
        const e0 = new Date(s0.getTime() + (dur - 1) * dayMs);
        tt.end_date = atEndOfDay(e0);
      } else if (tt.start_date && tt.end_date) {
        // If only start/end provided, compute duration using DATE-ONLY difference
        const s = new Date(tt.start_date); s.setHours(0,0,0,0);
        const e = new Date(tt.end_date); e.setHours(0,0,0,0);
        const days = Math.max(1, Math.round((e - s) / dayMs) + 1);
        tt.duration = days;
        tt.end_date = atEndOfDay(tt.end_date);
      }
    };
    const getFlatNode = (id) => {
      const arr = Array.isArray(window.__ganttFlatRaw) ? window.__ganttFlatRaw : (Array.isArray(raw) ? raw : []);
      return arr.find(n => String(n.id) === String(id));
    };
    const buildPayload = (task) => {
      const nodeFlat = getFlatNode(task.id);
      // Prefer the live task value edited in the lightbox; fallback to flat cache
      const totalQuantity = (typeof task.quantity === 'number')
        ? task.quantity
        : (nodeFlat && typeof nodeFlat.quantity !== 'undefined' ? Number(nodeFlat.quantity) : null);
      const parentId = nodeFlat && nodeFlat.parent != null ? String(nodeFlat.parent) : (task.parent != null ? String(task.parent) : null);
      // derive dependencies from existing flat if any (we don't change here)
      let dependencies = [];
      if (nodeFlat && Array.isArray(nodeFlat.dependencies)) {
        dependencies = nodeFlat.dependencies.map(d => ({ taskId: String(d.taskId ?? d.id ?? ''), type: String(d.type || 'FS').toUpperCase(), lag: Number(d.lag || 0) || 0 })).filter(d => d.taskId);
      }
      
      const payload = {
        text: task.text || '',
        start_date: toYMD(task.start_date),
        end_date: toYMD(task.end_date),
        duration: Number(task.duration) || 0,
        parent: parentId,
        workCategoryId: null,
        unit: task.unit || nodeFlat?.unit || null,
        lineNumber: task.lineNumber || '',
        line_number: task.line_number || '',
        assignedTo: task.assignedTo != null && task.assignedTo !== '' ? String(task.assignedTo) : null,
        subcontractor: task.assignedTo != null && task.assignedTo !== '' ? String(task.assignedTo) : null,
        dependencies
      };

      // Conditionally add quantity/progress fields
      if (ENABLE_QUANTITY_PROGRESS_UPDATE) {
        payload.progress = Number(task.progress) || 0;
        payload.quantity = totalQuantity;
        payload.weight = Number(task.weight) || 0;
        payload.completedQuantity = (typeof task.completedQuantity === 'number')
          ? task.completedQuantity
          : (task.completedQuantity ? Number(task.completedQuantity) : 0);
      }

      return payload;
    };

    

    // Prevent progress change and propagate parent date updates to descendants
    gantt.attachEvent('onBeforeTaskChanged', function(id, mode, task, original) {
      // Block any modification on Actual rows
      try { if (task?.nodeType === 'actual') return false; } catch(_) {}
      // Block progress drag entirely
      if (mode === gantt.config.drag_mode && mode === 'progress') return false;
      if (mode === 'progress') return false;

      // If parent types moved/resized -> update children
      const t = task || gantt.getTask(id);
      const isParentType = (tt) => tt && (tt.nodeType === 'project' || tt.nodeType === 'modularUnit' || tt.nodeType === 'workCategory');
      if (isParentType(t) && (mode === 'move' || mode === 'resize')) {
        try {
          const old = original || {};
          const oldStart = old.start_date instanceof Date ? old.start_date : (old.start_date ? new Date(old.start_date) : null);
          const newStart = t.start_date instanceof Date ? t.start_date : (t.start_date ? new Date(t.start_date) : null);
          
          const newEnd = t.end_date instanceof Date ? t.end_date : (t.end_date ? new Date(t.end_date) : null);
          const delta = (oldStart && newStart) ? (newStart - oldStart) : 0;
          // Traverse descendants
          const toUpdate = [];
          gantt.eachTask(function(child){
            if (child.id === id) return;
            // under this subtree
            let p = child.parent;
            let under = false;
            while (p) {
              if (String(p) === String(id)) { under = true; break; }
              const par = gantt.getTask(p);
              p = par ? par.parent : null;
            }
            if (!under) return;
            const c = gantt.getTask(child.id);
            let cs = c.start_date instanceof Date ? new Date(c.start_date) : (c.start_date ? new Date(c.start_date) : null);
            let ce = c.end_date instanceof Date ? new Date(c.end_date) : (c.end_date ? new Date(c.end_date) : null);
            if (mode === 'move' && delta) {
              if (cs) cs = new Date(cs.getTime() + delta);
              if (ce) ce = new Date(ce.getTime() + delta);
            }
            // Clamp to parent's new range if resizing
            if (mode === 'resize' && newStart && cs && cs < newStart) cs = new Date(newStart);
            if (mode === 'resize' && newEnd && ce && ce > newEnd) ce = new Date(newEnd);
            // Ensure duration >=1 day if needed
            if (cs && ce && ce < cs) ce = new Date(cs.getTime() + 24*60*60*1000);
            // Write back
            if (cs) c.start_date = atStartOfDay(cs);
            if (ce) c.end_date = atEndOfDay(ce);
            // recompute duration using DATE-ONLY boundaries to avoid +1 from time part
            if (c.start_date && c.end_date) {
              const dayMs = 24*60*60*1000;
              const s0 = new Date(c.start_date); s0.setHours(0,0,0,0);
              const e0 = new Date(c.end_date); e0.setHours(0,0,0,0);
              const days = Math.max(1, Math.round((e0 - s0) / dayMs) + 1);
              c.duration = days;
              // Also re-derive end strictly from start+duration-1 for stability
              const eCalc = new Date(s0.getTime() + (days - 1) * dayMs);
              c.end_date = atEndOfDay(eCalc);
            }
            toUpdate.push(c);
          }, id); // limit traversal under id
          // Apply updates visually
          toUpdate.forEach((c) => gantt.updateTask(c.id));
        } catch (_) {}
      }
      return true;
    });

    // Removed grid Split DOM delegation (moved to lightbox button)

    // Persist interactive changes (move/resize) using the service
    gantt.attachEvent('onAfterTaskDrag', function(id, mode, e){
      // If this is a pending-new task (created via Tambah Item), do NOT PUT it
      try {
        const key = String(id);
        if (pendingNewTasksRef.current.has(key)) {
          try { console.log('[Gantt] skip PUT on drag for pending-new', key); } catch(_) {}
          return true;
        }
      } catch(_) {}
      if (mode === 'progress') return; // blocked
      const t = gantt.getTask(id);
      normalizeTaskFullDay(t);
      const pid = (typeof projectId !== 'undefined' && projectId) ? projectId : (getFlatNode(id)?.projectId || (typeof window !== 'undefined' && window.__currentProjectId));
      const changed = [t];
      // Also persist descendants if parent changed
      const isParentType = (tt) => tt && (tt.nodeType === 'project' || tt.nodeType === 'modularUnit' || tt.nodeType === 'workCategory');
      if (isParentType(t)) {
        gantt.eachTask(function(child){
          if (String(child.id) === String(id)) return;
          // under subtree
          let p = child.parent; let under = false;
          while (p) { if (String(p) === String(id)) { under = true; break; } const par = gantt.getTask(p); p = par ? par.parent : null; }
          if (under) { const c = gantt.getTask(child.id); normalizeTaskFullDay(c); changed.push(c); }
        }, id);
      }
      const updates = changed.map(ct => ganttService.updateGanttTask(pid, ct.id, buildPayload(ct)));
      Promise.allSettled(updates).then((results) => {
        const anyRejected = results.some(r => r.status === 'rejected');
        gantt.message({ type: anyRejected ? 'warning' : 'success', text: anyRejected ? 'Some items failed to save' : 'Updated' });
        try { refresh(); } catch (_) {}
      }).catch(()=>{});
    });

    // Custom form blocks for numeric fields and predecessors
    if (!gantt.form_blocks.numeric3) {
      gantt.form_blocks.numeric3 = {
        render: function() {
          return '<div class="gnum-wrap"><input type="number" step="0.001" class="gnum-input" style="width:120px" /> <span>%</span></div>';
        },
        set_value: function(node, value) {
          if (!node) return;
          let input = node.querySelector('.gnum-input');
          if (!input) {
            node.innerHTML = '<div class="gnum-wrap"><input type="number" step="0.001" class="gnum-input" style="width:120px" /> <span>%</span></div>';
            input = node.querySelector('.gnum-input');
          }
          const n = parseFloat(value);
          if (input) input.value = isNaN(n) ? '' : n.toFixed(3);
        },
        get_value: function(node) {
          const input = node.querySelector('.gnum-input');
          if (!input) return 0;
          const v = parseFloat(input.value);
          return isNaN(v) ? 0 : parseFloat(v.toFixed(3));
        },
        focus: function(node) { node.querySelector('.gnum-input')?.focus(); }
      };
    }
    // Custom date picker section
    if (!gantt.form_blocks.date_picker) {
      gantt.form_blocks.date_picker = {
        render: function() {
          return (
            '<div class="date-picker-container">'
            +  '<div class="date-picker-row" style="display:flex; gap:12px; align-items:flex-end; margin-bottom:8px;">'
            +    '<div class="date-col" style="flex:1">'
            +      '<label style="display:block; margin-bottom:4px; font-weight:600;">Tanggal Mulai</label>'
            +      '<input type="date" class="start-date-input" style="width:100%; padding:6px; border:1px solid #ddd; border-radius:4px;" />'
            +    '</div>'
            +    '<div class="date-col" style="flex:1">'
            +      '<label style="display:block; margin-bottom:4px; font-weight:600;">Tanggal Selesai</label>'
            +      '<input type="date" class="end-date-input" style="width:100%; padding:6px; border:1px solid #ddd; border-radius:4px;" />'
            +    '</div>'
            +    '<div class="duration-col" style="flex:1">'
            +      '<label style="display:block; margin-bottom:4px; font-weight:600;">Durasi (hari)</label>'
            +      '<input type="number" min="1" class="duration-input" style="width:100%; padding:6px; border:1px solid #ddd; border-radius:4px;" readonly />'
            +    '</div>'
            +  '</div>'
            +  '<div class="date-note" style="background-color:rgba(255,235,59,0.3); padding:6px 8px; border-radius:4px; font-size:12px; color:#666; border-left:3px solid #ffc107;">'
            +    'Catatan: Tanggal Mulai diset pada jam 00:00:01 dan Tanggal Selesai diset pada jam 23:59:59.'
            +  '</div>'
            +'</div>'
          );
        },
        set_value: function(node, value, task) {
          if (!node) return;
          // ensure structure
          if (!node.querySelector('.start-date-input')) node.innerHTML = this.render();
          
          const startInput = node.querySelector('.start-date-input');
          const endInput = node.querySelector('.end-date-input');
          const durationInput = node.querySelector('.duration-input');
          
          // Convert dates to YYYY-MM-DD format for date inputs
          const toDateInputFormat = (date) => {
            if (!date) return '';
            const d = new Date(date);
            if (isNaN(d.getTime())) return '';
            return d.toISOString().split('T')[0];
          };
          
          // Set initial values
          startInput.value = toDateInputFormat(task.start_date);
          endInput.value = toDateInputFormat(task.end_date);
          durationInput.value = Math.max(1, Number(task.duration) || 1);
          
          // Parse YYYY-MM-DD format to Date object
          const parseDate = (dateStr) => {
            if (!dateStr) return null;
            return new Date(dateStr + 'T00:00:00'); // Add time to avoid timezone issues
          };

          // Auto-calculate duration when dates change
          const calculateDuration = () => {
            const start = parseDate(startInput.value);
            const end = parseDate(endInput.value);
            if (start && end && !isNaN(start.getTime()) && !isNaN(end.getTime()) && end >= start) {
              // Calculate duration in days (inclusive)
              const startTime = new Date(start.getFullYear(), start.getMonth(), start.getDate());
              const endTime = new Date(end.getFullYear(), end.getMonth(), end.getDate());
              const dayMs = 24 * 60 * 60 * 1000;
              const duration = Math.round((endTime - startTime) / dayMs) + 1;
              durationInput.value = Math.max(1, duration);
              // Update task object
              if (task) {
                task.duration = Math.max(1, duration);
                task.start_date = start;
                task.end_date = end;
              }
            }
          };
          
          // Auto-calculate end date when start date or duration changes
          const calculateEndDate = () => {
            const start = parseDate(startInput.value);
            const duration = parseInt(durationInput.value) || 1;
            if (start && !isNaN(start.getTime())) {
              const dayMs = 24 * 60 * 60 * 1000;
              const end = new Date(start.getTime() + (duration - 1) * dayMs);
              endInput.value = toDateInputFormat(end);
              // Update task object
              if (task) {
                task.start_date = start;
                task.end_date = end;
                task.duration = duration;
              }
            }
          };
          

          // Event listeners
          startInput.addEventListener('change', calculateEndDate);
          endInput.addEventListener('change', calculateDuration);
          durationInput.addEventListener('change', calculateEndDate);
        },
        get_value: function(node, task) {
          const startInput = node.querySelector('.start-date-input');
          const endInput = node.querySelector('.end-date-input');
          const durationInput = node.querySelector('.duration-input');
          
          // Parse YYYY-MM-DD format
          const parseInputDate = (dateStr) => {
            if (!dateStr) return null;
            return new Date(dateStr + 'T00:00:00');
          };

          const startDate = startInput.value ? parseInputDate(startInput.value) : task.start_date;
          const endDate = endInput.value ? parseInputDate(endInput.value) : task.end_date;
          
          return {
            start_date: startDate ? new Date(startDate.getTime() + 1000) : task.start_date, // Add 1 second for 00:00:01
            end_date: endDate ? new Date(endDate.getTime() + 24*60*60*1000 - 1000) : task.end_date, // Add 23:59:59
            duration: Math.max(1, parseInt(durationInput.value) || 1)
          };
        },
        focus: function(node) { 
          node.querySelector('.start-date-input')?.focus(); 
        }
      };
    }

    // Composite metrics row: Bobot, Quantity, Progress bar (3 columns)
    if (!gantt.form_blocks.metrics_row) {
      gantt.form_blocks.metrics_row = {
        render: function() {
          const qtyProgressDisabled = !ENABLE_QUANTITY_PROGRESS_UPDATE;
          const qtyProgressDisabledAttr = qtyProgressDisabled ? ' disabled readonly' : '';
          const qtyProgressDisabledStyle = qtyProgressDisabled ? ' opacity:0.6; cursor:not-allowed;' : '';
          
          return (
            '<div class="metrics-row" style="display:flex; gap:12px; align-items:flex-end;">'
            +  '<div class="mcol mcol-weight" style="flex:1">'
            +    '<label style="display:block; margin-bottom:4px; font-weight:600;">Bobot (%)</label>'
            +    '<div class="gnum-wrap"><input type="number" step="0.001" class="gnum-input" style="width:100%" /></div>'
            +  '</div>'
            +  '<div class="mcol mcol-qty-total" style="flex:1">'
            +    '<label style="display:block; margin-bottom:4px; font-weight:600;">Volume</label>'
            +    '<input type="number" step="0.01" class="gqty-total" style="width:100%" />'
            +  '</div>'
            +  '<div class="mcol mcol-qty-input" style="flex:1">'
            +    '<label style="display:block; margin-bottom:4px; font-weight:600;">Volume Progress</label>'
            +    `<input type="number" step="0.01" class="gqty-input" style="width:100%;${qtyProgressDisabledStyle}"${qtyProgressDisabledAttr} />`
            +  '</div>'
            +  '<div class="mcol mcol-prog" style="flex:1">'
            +    '<label style="display:block; margin-bottom:4px; font-weight:600;">Progress</label>'
            +    '<div class="gbar-wrap" style="height:32px; background:#eceff1; border-radius:6px; overflow:hidden; position:relative">'
            +      '<div class="gbar-fill" style="height:100%; width:0%;"></div>'
            +      '<div class="gbar-text" style="position:absolute; inset:0; display:flex; align-items:center; justify-content:center; font-weight:600; color:#fff;">0%</div>'
            +    '</div>'
            +  '</div>'
            +'</div>'
          );
        },
        set_value: function(node, value, task) {
          if (!node) return;
          // ensure structure
          if (!node.querySelector('.gnum-input')) node.innerHTML = this.render();
          const weightInput = node.querySelector('.gnum-input');
          const qtyTotal = node.querySelector('.gqty-total');
          const qtyInput = node.querySelector('.gqty-input');
          const bar = node.querySelector('.gbar-fill');
          const barText = node.querySelector('.gbar-text');
          // Update Volume label with unit dynamically (e.g., m3, unit)
          try {
            const volLabel = node.querySelector('.mcol-qty-total label');
            // Prefer task.unit; fallback to flat cache if available
            let unit = task?.unit;
            if (!unit) {
              try {
                const arr = Array.isArray(window.__ganttFlatRaw) ? window.__ganttFlatRaw : [];
                const flat = arr.find(n => String(n.id) === String(task?.id));
                unit = flat?.unit;
              } catch(_) {}
            }
            if (volLabel) volLabel.textContent = unit ? `Volume (${unit})` : 'Volume';
          } catch(_) {}

          const w = parseFloat(task?.weight);
          weightInput.value = isNaN(w) ? '' : w.toFixed(3);
          // Quantity (total) editable, default 0 (support multiple aliases and flat cache)
          let qTotalVal =  (typeof task?.quantity === 'number') ? task.quantity : NaN;
          if (isNaN(qTotalVal)) {
            const tAlt = task?.totalQuantity ?? task?.qty ?? task?.total_qty;
            if (tAlt != null && tAlt !== '') qTotalVal = Number(tAlt);
          }
          if (isNaN(qTotalVal)) {
            try {
              const arr = Array.isArray(window.__ganttFlatRaw) ? window.__ganttFlatRaw : [];
              const flat = arr.find(n => String(n.id) === String(task?.id));
              if (flat) {
                const qraw = flat.quantity ?? flat.totalQuantity ?? flat.qty ?? flat.total_qty;
                if (qraw != null && qraw !== '') qTotalVal = Number(qraw);
              }
            } catch(_) {}
          }
          const qTotal = qTotalVal;
          qtyTotal.value = isNaN(qTotal) ? '0' : qTotal.toFixed(2);
          // Quantity Progress editable, defaults to completedQuantity or ratio*total (support aliases)
          let completed = (typeof task?.completedQuantity === 'number') ? task.completedQuantity : NaN;
          if (isNaN(completed)) {
            const cAlt = task?.completed_qty ?? task?.actualQuantity;
            if (cAlt != null && cAlt !== '') completed = Number(cAlt);
          }
          if (isNaN(completed)) {
            try {
              const arr = Array.isArray(window.__ganttFlatRaw) ? window.__ganttFlatRaw : [];
              const flat = arr.find(n => String(n.id) === String(task?.id));
              if (flat) {
                const craw = flat.completedQuantity ?? flat.completed_qty ?? flat.actualQuantity;
                if (craw != null && craw !== '') completed = Number(craw);
              }
            } catch(_) {}
          }
          const totalNum = parseFloat(qtyTotal.value);
          let qpVal = !isNaN(completed) ? completed : NaN;
          if (isNaN(qpVal)) {
            const prog = Number(task?.progress || 0);
            const ratio = prog <= 1 ? prog : prog/100;
            qpVal = isFinite(totalNum) ? (ratio * (isNaN(totalNum) ? 0 : totalNum)) : 0;
          }
          qtyInput.value = (isNaN(qpVal) ? 0 : qpVal).toFixed(2);
          const p = Number(task?.progress || 0);
          const pct = p <= 1 ? Math.round(p * 100) : Math.round(p);
          if (bar) bar.style.width = Math.max(0, Math.min(100, pct)) + '%';
          if (barText) barText.textContent = pct + '%';
          // Set color thresholds
          if (bar) {
            let color = '#fb8c00'; // orange 0-50
            if (pct >= 100) color = '#2e7d32'; // green
            else if (pct >= 51) color = '#1e88e5'; // blue
            bar.style.background = color;
            if (barText) { barText.style.color = '#ffffff'; barText.style.fontWeight = '700'; }
          }

          // Guard: clamp Quantity Progress to [0, Quantity], recompute progress, keep status in sync
          const clampQtyProgress = () => {
            if (!qtyInput) return;
            const total = parseFloat(qtyTotal?.value);
            let val = parseFloat(qtyInput.value);
            if (isNaN(val)) return;
            if (!isNaN(total)) {
              if (val > total) val = total;
            }
            if (val < 0) val = 0;
            qtyInput.value = isNaN(val) ? '' : val.toFixed(2);
            // Update task.progress and progress bar + Status select
            const ratio = !isNaN(total) && total > 0 ? Math.max(0, Math.min(1, val / total)) : 0;
            if (task) task.progress = ratio;
            if (task) task.completedQuantity = val;
            if (task && !isNaN(total)) task.quantity = total;
            const pctNow = Math.round(ratio * 100);
            if (bar) bar.style.width = pctNow + '%';
            if (barText) barText.textContent = pctNow + '%';
            if (bar) {
              let color = '#fb8c00';
              if (pctNow >= 100) color = '#2e7d32';
              else if (pctNow >= 51) color = '#1e88e5';
              bar.style.background = color;
              if (barText) { barText.style.color = '#ffffff'; barText.style.fontWeight = '700'; }
            }
            // Sync Status select in lightbox
            try {
              const box = document.querySelector('.gantt_cal_light');
              if (box) {
                let statusSel = null;
                const sections = box.querySelectorAll('.gantt_cal_lsection');
                sections.forEach(sec => {
                  const label = sec.querySelector('label');
                  if (label && /Status/i.test(label.textContent || '')) {
                    const container = sec.nextElementSibling;
                    const sel = container ? container.querySelector('select') : null;
                    if (sel) statusSel = sel;
                  }
                });
                if (statusSel) {
                  const statusVal = pctNow === 0 ? 'todo' : (pctNow >= 100 ? 'done' : 'inprogress');
                  statusSel.value = statusVal;
                  if (window.$ && window.$.fn && window.$.fn.select2) {
                    window.$(statusSel).val(statusVal).trigger('change.select2');
                  }
                  if (task) task.status = statusVal;
                }
              }
            } catch (_) {}
          };
          const clampTotal = () => {
            if (!qtyTotal) return;
            let t = parseFloat(qtyTotal.value);
            if (isNaN(t) || t < 0) t = 0;
            qtyTotal.value = t.toFixed(2);
            // Re-apply clamp on progress when total changes
            clampQtyProgress();
          };
          if (qtyInput && !qtyInput.dataset.clampBound) {
            qtyInput.addEventListener('keyup', clampQtyProgress);
            qtyInput.addEventListener('change', clampQtyProgress);
            qtyInput.dataset.clampBound = '1';
          }
          if (qtyTotal && !qtyTotal.dataset.totalBound) {
            qtyTotal.addEventListener('keyup', clampTotal);
            qtyTotal.addEventListener('change', clampTotal);
            qtyTotal.dataset.totalBound = '1';
          }
        },
        get_value: function(node, task) {
          // read inputs and set back to task; return weight as mapped value
          const weightInput = node.querySelector('.gnum-input');
          const qtyInput = node.querySelector('.gqty-input');
          const qtyTotal = node.querySelector('.gqty-total');
          const w = parseFloat(weightInput?.value);
          const q = parseFloat(qtyInput?.value);
          const qt = parseFloat(qtyTotal?.value);
          if (task) {
            task.completedQuantity = isNaN(q) ? 0 : parseFloat(q.toFixed(2));
            task.quantity = isNaN(qt) ? 0 : parseFloat(qt.toFixed(2));
          }
          return isNaN(w) ? 0 : parseFloat(w.toFixed(3));
        },
        focus: function(node) { node.querySelector('.gnum-input')?.focus(); }
      };
    }
    if (!gantt.form_blocks.predecessor_row) {
      gantt.form_blocks.predecessor_row = {
        render: function() {
          const html = '<div class="gpred-container" style="min-height:120px;">'
            + '<div class="gpred-header" style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">'
    + '<div class="gpred-title" style="font-weight:600;">Predecessors</div>'
            + '<button type="button" class="gpred-add-btn" style="background:#2196f3; color:white; border:none; padding:4px 8px; border-radius:3px; cursor:pointer; font-size:12px;">+ Add</button>'
            + '</div>'
            + '<div class="gpred-list" style="max-height:200px; overflow-y:auto; overflow-x:hidden;"></div>'
            + '</div>';
          return html;
        },
        set_value: function(node, value, task) {
          if (!node) {
            console.warn('Predecessor set_value: node is null');
            return;
          }
          
          // Try simple fallback first if render fails
          try {
            // Direct HTML generation to avoid context issues
            const renderedHTML = '<div class="gpred-container" style="min-height:120px;">'
              + '<div class="gpred-header" style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">'
    + '<div class="gpred-title" style="font-weight:600;">Predecessors</div>'
              + '<button type="button" class="gpred-add-btn" style="background:#2196f3; color:white; border:none; padding:4px 8px; border-radius:3px; cursor:pointer; font-size:12px;">+ Add</button>'
              + '</div>'
              + '<div class="gpred-list" style="max-height:200px; overflow-y:auto; overflow-x:hidden;"></div>'
              + '</div>';
              
            node.innerHTML = renderedHTML;
          } catch(e) {
            console.error('Error rendering predecessor structure:', e);
            // Fallback to simple HTML
            node.innerHTML = `
              <div style="padding: 10px; border: 1px solid #ddd; border-radius: 4px;">
                <div style="color: #666; font-size: 12px;">Predecessor functionality temporarily unavailable</div>
              </div>
            `;
            return;
          }
          
          // Get references immediately
          const finalContainer = node.querySelector('.gpred-list');
          const finalAddBtn = node.querySelector('.gpred-add-btn');
          
          if (!finalContainer || !finalAddBtn) {
            console.error('Failed to create predecessor elements after render');
            // Fallback to simple structure
            node.innerHTML = `
              <div style="padding: 10px; border: 1px solid #ddd; border-radius: 4px;">
                <div style="color: #666; font-size: 12px;">Unable to load predecessor interface</div>
              </div>
            `;
            return;
          }
          
          // Continue with setup inline
          // Build available tasks list (exclude self and descendants)
          const availableTasks = [];
          try {
            gantt.eachTask(function(t){
              if (String(t.id) === String(task.id)) return;
              if (t.nodeType === 'task' || t.type === gantt.config.types.task) {
                availableTasks.push({ id: String(t.id), text: String(t.text || `Task ${t.id}`) });
              }
            });
          } catch (_) {}
          
          if (!availableTasks.length && typeof window !== 'undefined' && Array.isArray(window.__ganttFlatRaw)) {
            window.__ganttFlatRaw
              .filter(n => (n && n.type === 'task' && String(n.id) !== String(task.id)))
              .forEach(n => availableTasks.push({ id: String(n.id), text: String(n.text || n.name || `Task ${n.id}`) }));
          }
          availableTasks.sort((a,b) => a.text.localeCompare(b.text));
          
          // Get existing dependencies
          let dependencies = [];
          try {
            const flatNode = (Array.isArray(window.__ganttFlatRaw) ? window.__ganttFlatRaw : []).find(n => String(n.id) === String(task.id));
            if (flatNode && Array.isArray(flatNode.dependencies)) {
              dependencies = flatNode.dependencies.map(d => ({
                taskId: String(d.taskId ?? d.id ?? ''),
                type: String(d.type || 'FS').toUpperCase(),
                lag: Number(d.lag || 0) || 0
              })).filter(d => d.taskId);
            }
          } catch(_) {}
          
          // If no dependencies from flat data, try parsing from pred string
          if (!dependencies.length && value) {
            const predStr = String(value || '');
            // Handle multiple predecessors separated by comma or semicolon
            const predParts = predStr.split(/[,;]/).filter(p => p.trim());
            dependencies = predParts.map(part => {
              const m = part.trim().match(/^(\d+)(FS|FF|SS|SF)?(?::(-?\d+))?$/i);
              if (m) {
                return {
                  taskId: m[1],
                  type: (m[2] || 'FS').toUpperCase(),
                  lag: parseInt(m[3] || '0', 10) || 0
                };
              }
              return null;
            }).filter(d => d);
          }
          
          // Create predecessor row function
          const createPredRow = (dep = {}) => {
            const row = document.createElement('div');
            row.className = 'gpred-item';
            row.style.cssText = 'display:flex; gap:6px; align-items:center; margin-bottom:6px; padding:6px; border:1px solid #e0e0e0; border-radius:4px; background:#f9f9f9; overflow:hidden;';
            
            row.innerHTML = 
              '<select class="gpred-task" style="width:375px; padding:4px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">'
              +   '<option value="">-- Select Task --</option>'
              + '</select>'
              + '<select class="gpred-type" style="width:60px; padding:4px; flex-shrink:0;">'
              +   '<option value="FS">FS</option>'
              +   '<option value="SS">SS</option>'
              +   '<option value="FF">FF</option>'
              +   '<option value="SF">SF</option>'
              + '</select>'
              + '<input class="gpred-lag" type="number" placeholder="Lag" style="width:60px; padding:4px; flex-shrink:0;" />'
              + '<button type="button" class="gpred-remove" style="background:#f44336; color:white; border:none; padding:4px 6px; border-radius:3px; cursor:pointer; font-size:11px; width:24px; flex-shrink:0;">×</button>';
            
            // Populate task options
            const taskSel = row.querySelector('.gpred-task');
            availableTasks.forEach(task => {
              const option = document.createElement('option');
              option.value = task.id;
              // Truncate long task names for better display
              const taskText = task.text.length > 30 ? task.text.substring(0, 27) + '...' : task.text;
              option.textContent = `${taskText} (ID: ${task.id})`;
              option.title = `${task.text} (ID: ${task.id})`; // Full text in tooltip
              taskSel.appendChild(option);
            });
            
            // Initialize Select2 if available
            if (typeof window !== 'undefined' && window.$ && window.$.fn && window.$.fn.select2) {
              try {
                window.$(taskSel).select2({
                  placeholder: '-- Select Task --',
                  allowClear: true,
                  width: 'style', // Use the width from the original element's style
                  dropdownAutoWidth: false,
                  templateResult: function(option) {
                    if (!option.id) return option.text;
                    return window.$('<span title="' + option.title + '">' + option.text + '</span>');
                  }
                });
                
                // Force the Select2 container to use fixed width
                const select2Container = window.$(taskSel).next('.select2-container');
                if (select2Container.length) {
                  select2Container.css({
                    'width': '375px !important'
                  });
                }
              } catch(e) {
                console.warn('Select2 initialization failed:', e);
              }
            }
            
            // Set values
            if (dep.taskId) {
              taskSel.value = dep.taskId;
              // Trigger Select2 update if initialized
              if (typeof window !== 'undefined' && window.$ && window.$(taskSel).hasClass('select2-hidden-accessible')) {
                window.$(taskSel).trigger('change');
              }
            }
            if (dep.type) row.querySelector('.gpred-type').value = dep.type;
            if (dep.lag !== undefined) row.querySelector('.gpred-lag').value = dep.lag;
            
            return row;
          };
          
          // Clear and populate container
          finalContainer.innerHTML = '';
          
          // Add existing dependencies
          if (dependencies.length) {
            dependencies.forEach(dep => {
              finalContainer.appendChild(createPredRow(dep));
            });
          } else {
            // Add one empty row by default
            finalContainer.appendChild(createPredRow());
          }
          
          // Apply consistent styling to all existing rows (fix for first load)
          const allRows = finalContainer.querySelectorAll('.gpred-item');
          allRows.forEach(row => {
            row.style.cssText = 'display:flex; gap:6px; align-items:center; margin-bottom:6px; padding:6px; border:1px solid #e0e0e0; border-radius:4px; background:#f9f9f9; overflow:hidden;';
            
            // Update task select styling
            const taskSelect = row.querySelector('.gpred-task');
            if (taskSelect) {
              taskSelect.style.cssText = 'width:375px; padding:4px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;';
              
              // If Select2 is already initialized, update its container styling too
              if (typeof window !== 'undefined' && window.$ && window.$(taskSelect).hasClass('select2-hidden-accessible')) {
                const select2Container = window.$(taskSelect).next('.select2-container');
                if (select2Container.length) {
                  select2Container.css({
                    'width': '375px !important'
                  });
                }
              }
            }
            
            // Update type select styling
            const typeSelect = row.querySelector('.gpred-type');
            if (typeSelect) {
              typeSelect.style.cssText = 'width:60px; padding:4px; flex-shrink:0;';
            }
            
            // Update lag input styling
            const lagInput = row.querySelector('.gpred-lag');
            if (lagInput) {
              lagInput.style.cssText = 'width:60px; padding:4px; flex-shrink:0;';
            }
            
            // Update remove button styling
            const removeBtn = row.querySelector('.gpred-remove');
            if (removeBtn) {
              removeBtn.style.cssText = 'background:#f44336; color:white; border:none; padding:4px 6px; border-radius:3px; cursor:pointer; font-size:11px; width:24px; flex-shrink:0;';
            }
          });
          
          // Store createPredRow function on node for event delegation
          node._createPredRow = createPredRow;
          
          // Use event delegation to avoid duplicate listeners
          if (!node.hasAttribute('data-pred-initialized')) {
            node.addEventListener('click', (e) => {
              if (e.target.classList.contains('gpred-add-btn')) {
                const container = node.querySelector('.gpred-list');
                if (container && node._createPredRow) {
                  container.appendChild(node._createPredRow());
                }
              } else if (e.target.classList.contains('gpred-remove')) {
                e.target.closest('.gpred-item')?.remove();
              }
            });
            node.setAttribute('data-pred-initialized', 'true');
          }
        },
        get_value: function(node) {
          if (!node) return '';
          
          const container = node.querySelector('.gpred-list');
          if (!container) return '';
          
          const rows = container.querySelectorAll('.gpred-item');
          if (!rows || rows.length === 0) return '';
          
          const dependencies = [];
          
          rows.forEach(row => {
            try {
              const taskSelect = row.querySelector('.gpred-task');
              let taskId = taskSelect?.value;
              
              // Handle Select2 values
              if (typeof window !== 'undefined' && window.$ && window.$(taskSelect).hasClass('select2-hidden-accessible')) {
                taskId = window.$(taskSelect).val();
              }
              const type = row.querySelector('.gpred-type')?.value || 'FS';
              const lag = parseInt(row.querySelector('.gpred-lag')?.value || '0', 10) || 0;
              
              if (taskId) {
                dependencies.push(`${taskId}${type}:${lag}`);
              }
            } catch(e) {
              console.warn('Error reading predecessor row:', e);
            }
          });
          
          // Return comma-separated string for backward compatibility
          return dependencies.join(',');
        },
        focus: function(node) { 
          const firstTask = node.querySelector('.gpred-task');
          if (firstTask) firstTask.focus();
        }
      };
    }

    // Custom lightbox configuration
    gantt.config.lightbox.sections = [
      { name: 'description', height: 70, map_to: 'text', type: 'textarea', focus: true },
      { name: 'subcontractor', height: 28, map_to: 'assignedTo', type: 'select', options: [] },
      { name: 'status', height: 28, map_to: 'status', type: 'select', options: [
        { key: 'todo', label: 'To Do' },
        { key: 'inprogress', label: 'In Progress' },
        { key: 'done', label: 'Done' }
      ]},
      { name: 'time', height: 72, type: 'date_picker', map_to: 'auto' },
      { name: 'metrics', height: 90, map_to: 'weight', type: 'metrics_row' },
      { name: 'predecessors', height: 150, map_to: 'pred', type: 'predecessor_row' }
    ];

    // Custom labels for lightbox sections
    gantt.locale.labels.section_description = 'Deskripsi';
    gantt.locale.labels.section_subcontractor = 'Subcontractor';
    gantt.locale.labels.section_status = 'Status';
    // Hide default header row for time and predecessors (we render our own headers)
    gantt.locale.labels.section_time = ' ';
    gantt.locale.labels.section_weight = 'Bobot (%)';
    gantt.locale.labels.section_quantity = 'Quantity Progress (Unit)';
    gantt.locale.labels.section_progress = 'Progress (%)';
    gantt.locale.labels.section_metrics = ' '; // we render internal labels per column
    gantt.locale.labels.section_predecessors = ' ';

    // Custom buttons
    gantt.locale.labels.icon_save = ' Simpan';
    gantt.locale.labels.icon_cancel = ' Batal';
    gantt.locale.labels.icon_delete = ' Hapus';

    // Handle status changes and persist via API (detach existing to avoid duplicates)
    try {
      if (gantt._hakiki_update_hook_id) { 
        try { gantt.detachEvent(gantt._hakiki_update_hook_id); } catch(_) {} 
      }
    } catch(_) {}
    gantt._hakiki_update_hook_id = gantt.attachEvent('onLightboxSave', function(id, task, is_new) {
      try {
        // Sanitize task name before saving
        try { if (task && task.text) task.text = stripVolumeSuffix(task.text); } catch(_) {}
        // Skip if this is a new task - let the other handler deal with it
        const key = String(id);
        const inPending = pendingNewTasksRef.current.has(key);
        if (is_new || inPending) {
          return true; // Let the new task handler process this
        }

        // Sync status with progress
        if (task.status === 'todo') task.progress = 0;
        else if (task.status === 'done') task.progress = 1;
        else if (task.status === 'inprogress' && task.progress === 0) task.progress = 0.5;

        // Get values from lightbox form sections
        let weight = 0;
        let quantity = 0;
        
        // Try to get weight and quantity from lightbox sections
        try {
          const metricsSection = gantt.getLightboxSection && gantt.getLightboxSection('metrics');
          if (metricsSection && metricsSection.getValue) {
            const metricsValue = metricsSection.getValue();
            if (metricsValue) {
              weight = parseFloat(metricsValue.weight) || parseFloat(task.weight) || 0;
              quantity = parseFloat(metricsValue.quantity) || parseFloat(task.quantity) || 0;
            }
          }
        } catch(e) {
          console.log('[DEBUG] Could not get metrics from lightbox, using task values:', e);
          weight = parseFloat(task.weight) || 0;
          quantity = parseFloat(task.quantity) || 0;
        }

        // Preserve existing completedQuantity from task data
        let completedQuantity = (typeof task.completedQuantity === 'number') 
          ? task.completedQuantity 
          : (task.completedQuantity ? Number(task.completedQuantity) : 0);

        // Pull duration from lightbox 'time' section to avoid +1 drift
        let saveDuration = Number(task.duration) || 0;
        try {
          const timeSec = gantt.getLightboxSection && gantt.getLightboxSection('time');
          if (timeSec && timeSec.getValue) {
            const tv = timeSec.getValue();
            if (tv && typeof tv.duration !== 'undefined') {
              saveDuration = Number(tv.duration) || saveDuration;
            } else if (tv?.start_date && tv?.end_date) {
              const dayMs = 24*60*60*1000;
              const s0 = new Date(tv.start_date); s0.setHours(0,0,0,0);
              const e0 = new Date(tv.end_date); e0.setHours(0,0,0,0);
              saveDuration = Math.max(1, Math.round((e0 - s0) / dayMs) + 1);
            }
          }
        } catch(_) {}
        if (!isFinite(saveDuration) || saveDuration <= 0) saveDuration = 1;
        // Ensure task model reflects the computed duration (for UI consistency)
        // Dates to YYYY-MM-DD (support Date or string)
        const fmt = gantt.date.date_to_str('%Y-%m-%d');
        const toYMD = (v) => {
          if (!v) return null;
          if (v instanceof Date) return fmt(v);
          if (typeof v === 'string') return v.slice(0, 10);
          const d = new Date(v);
          return isNaN(d.getTime()) ? null : fmt(d);
        };
        let sd = toYMD(task.start_date);
        let ed = toYMD(task.end_date);
        try {
          if (sd && saveDuration) {
            const s = new Date(`${sd} 00:00:01`);
            const e = new Date(s.getTime() + (Math.max(1, Math.round(saveDuration)) - 1) * 24*60*60*1000);
            e.setHours(23,59,59,0);
            const fmtDT = gantt.date.date_to_str('%Y-%m-%d');
            ed = fmtDT(e);
          }
        } catch(_) {}

        // Find original node to get total quantity and parent/category id
        const nodeFlat = (Array.isArray(window.__ganttFlatRaw) ? window.__ganttFlatRaw : Array.isArray(raw) ? raw : []).find(n => String(n.id) === String(id));
        const parentId = nodeFlat && nodeFlat.parent != null ? String(nodeFlat.parent) : (task.parent != null ? String(task.parent) : null);

        // Dependencies from pred: format "<id><TYPE>:<lag>" or multiple comma-separated
        let dependencies = [];
        if (task.pred) {
          const predStr = String(task.pred);
          // Handle multiple predecessors separated by comma
          const predParts = predStr.split(',').filter(p => p.trim());
          
          predParts.forEach(part => {
            const m = part.trim().match(/^(\d+)(FS|FF|SS|SF)?(?::(-?\d+))?$/i);
            if (m) {
              dependencies.push({ 
                taskId: m[1], 
                type: (m[2] || 'FS').toUpperCase(), 
                lag: parseInt(m[3] || '0', 10) || 0 
              });
            }
          });
        } else if (nodeFlat && Array.isArray(nodeFlat.dependencies)) {
          // fallback to existing
          dependencies = nodeFlat.dependencies.map(d => ({ 
            taskId: String(d.taskId ?? d.id ?? ''), 
            type: String(d.type || 'FS').toUpperCase(), 
            lag: Number(d.lag || 0) || 0 
          })).filter(d => d.taskId);
        }

        const payload = {
          text: stripVolumeSuffix(task.text || ''),
          start_date: sd || null,
          end_date: ed || null,
          duration: Math.max(1, Math.round(saveDuration)),
          parent: parentId,
          workCategoryId: null,
          unit: task.unit || nodeFlat?.unit || null,
          lineNumber: task.lineNumber || '',
          line_number: task.line_number || '',
          assignedTo: task.assignedTo != null && task.assignedTo !== '' ? String(task.assignedTo) : null,
          subcontractor: task.assignedTo != null && task.assignedTo !== '' ? String(task.assignedTo) : null,
          dependencies
        };

        // Always include weight and quantity in payload (not conditional)
        payload.progress = Number(task.progress) || 0;
        payload.weight = weight;
        payload.quantity = quantity;
        payload.completedQuantity = completedQuantity;

        const pid = projectId || nodeFlat?.projectId || (typeof window !== 'undefined' && window.__currentProjectId);
        
        console.log('[DEBUG] Sending update payload:', payload);
        
        ganttService.updateGanttTask(pid, id, payload)
          .then((data) => {
            // Some backends wrap response; treat any truthy data as success
            if (data && data.success === false) throw new Error('API returned error');
            gantt.message({ type: 'success', text: 'Task saved' });
            gantt.hideLightbox();
            
            // Debounced refresh to prevent multiple calls
            if (window.__gantt_refresh_timeout) {
              clearTimeout(window.__gantt_refresh_timeout);
            }
            window.__gantt_refresh_timeout = setTimeout(() => {
              try { refresh(); } catch (_) {}
            }, 300);
          })
          .catch((err) => {
            console.error('Save error', err);
            gantt.message({ type: 'error', text: 'Failed to save task' });
          });
      } catch (e) {
        console.error('onLightboxSave error', e);
      }
      return true;
    });

    // Attach our delete handler directly to onLightboxDelete (with detach to avoid duplicates)
    try {
      if (gantt._hakiki_delete_hook_id) { try { gantt.detachEvent(gantt._hakiki_delete_hook_id); } catch(_) {} }
    } catch(_) {}
    gantt._hakiki_delete_hook_id = gantt.attachEvent('onLightboxDelete', function(id){
      const ok = window.confirm('Apakah Anda yakin ingin menghapus task ini?');
      if (!ok) return false;
      try { gantt.message({ type: 'info', text: 'Menghapus task...' }); } catch(_) {}
      const taskId = String(id);
      ganttService.deleteTaskById(taskId)
        .then(() => {
          try { gantt.hideLightbox(); } catch(_) {}
          try { const cover = document.querySelector('.gantt_cal_cover'); if (cover) cover.remove(); } catch(_) {}
          try { gantt.deleteTask(id); } catch(_) {}
          try { gantt.message({ type: 'success', text: 'Task dihapus' }); } catch(_) {}
          try { refresh(); } catch(_) {}
        })
        .catch((e) => {
          console.error('Delete task failed', e);
          try { gantt.message({ type: 'error', text: 'Gagal menghapus task' }); } catch(_) {}
        });
      return false; // prevent built-in default
    });

    // Optional extra strip only if supported by this Gantt version
    try {
      if (typeof gantt.addTaskLayer === 'function') {
        const getProgressColor = (pct) => {
          if (pct >= 100) return '#2e7d32';
          if (pct >= 51) return '#1e88e5';
          return '#fb8c00';
        };
        gantt.addTaskLayer(function(task){
          if (!task || task.nodeType !== 'task') return null;
          const sizes = gantt.getTaskPosition(task);
          if (!sizes) return null;
          const el = document.createElement('div');
          el.className = 'custom-progress-strip';
          const pct = Math.max(0, Math.min(100, (Number(task.progress || 0) <= 1 ? Number(task.progress||0)*100 : Number(task.progress||0))));
          const w = Math.max(0, Math.round(sizes.width * (pct/100)));
          el.style.position = 'absolute';
          el.style.left = sizes.left + 'px';
          el.style.top = (sizes.top + sizes.height + 2) + 'px';
          el.style.width = w + 'px';
          el.style.height = '4px';
          el.style.background = getProgressColor(pct);
          el.style.borderRadius = '2px';
          return el;
        });
      }
    } catch(_) {}

    gantt.plugins({ critical_path: true });

    gantt.init(ganttRef.current);

    // Ensure any legacy global split hook routes to the new dialog
    // This guards cases where another handler calls window.splitTaskFromGantt(taskId)
    try {
      window.splitTaskFromGantt = (taskId) => {
        try {
          const id = taskId || (gantt.getState && gantt.getState().lightbox && gantt.getState().lightbox.id) || lastLightboxTaskId.current || gantt.getSelectedId();
          if (!id) { try { gantt.message({ type:'error', text:'Tidak dapat menentukan task untuk split.' }); } catch(_) {} ; return; }
          const t = gantt.getTask(id);
          if (!t || t.nodeType !== 'task') { try { gantt.message({ type:'warning', text:'Hanya pekerjaan (task) yang bisa di-split.' }); } catch(_) {} ; return; }
          const taskPayload = {
            id: t.id,
            name: t.text || t.name || '',
            text: t.text || '',
            start_date: t.start_date instanceof Date ? t.start_date : (t.start_date ? new Date(t.start_date) : null),
            end_date: t.end_date instanceof Date ? t.end_date : (t.end_date ? new Date(t.end_date) : null),
            quantity: Number.isFinite(Number(t.quantity)) ? Number(t.quantity) : 0,
            progress: Number.isFinite(Number(t.progress)) ? Number(t.progress) : 0,
            unit: t.unit || null,
          };
          try { gantt.hideLightbox(); } catch(_) {}
          setTaskToSplit(taskPayload);
          setSplitDialogOpen(true);
        } catch (err) { console.error('splitTaskFromGantt override failed:', err); }
      };
    } catch(_) {}

    // Lightbox: handle custom Split button
    try { if (gantt._hakiki_split_btn_hook_id) { try { gantt.detachEvent(gantt._hakiki_split_btn_hook_id); } catch(_) {} } } catch(_) {}
    gantt._hakiki_split_btn_hook_id = gantt.attachEvent('onLightboxButton', function(button_id){
      console.log('[DEBUG] Lightbox button clicked:', button_id);
      if (button_id !== 'gantt_split_btn') return true;
      // NEW: Route to React TaskSplitDialog (short-circuit legacy flow)
      try {
        let id = null;
        try { id = (gantt.getState && gantt.getState().lightbox && gantt.getState().lightbox.id) ? gantt.getState().lightbox.id : null; } catch(_) {}
        if (!id && lastLightboxTaskId.current) id = lastLightboxTaskId.current;
        if (!id) { try { id = gantt._lightbox_id; } catch(_) {} }
        if (!id) { try { id = gantt.getSelectedId(); } catch(_) {} }
        if (!id) { try { gantt.message({ type:'error', text:'Tidak dapat menentukan task yang sedang diedit.' }); } catch(_) {} ; return false; }
        const t = gantt.getTask(id);
        if (!t || t.nodeType !== 'task') { try { gantt.message({ type:'warning', text:'Hanya pekerjaan (task) yang bisa di-split.' }); } catch(_) {} ; return false; }
        const taskPayload = {
          id: t.id,
          name: t.text || t.name || '',
          text: t.text || '',
          start_date: t.start_date instanceof Date ? t.start_date : (t.start_date ? new Date(t.start_date) : null),
          end_date: t.end_date instanceof Date ? t.end_date : (t.end_date ? new Date(t.end_date) : null),
          quantity: Number.isFinite(Number(t.quantity)) ? Number(t.quantity) : 0,
          progress: Number.isFinite(Number(t.progress)) ? Number(t.progress) : 0,
          unit: t.unit || null,
        };
        try { gantt.hideLightbox(); } catch(_) {}
        setTaskToSplit(taskPayload);
        setSplitDialogOpen(true);
        return false; // prevent default legacy flow
      } catch (err) {
        console.error('Failed to open split dialog:', err);
        return false;
      }
    });

    // Persist drag-and-drop ordering/moves
    try {
      if (gantt._hakiki_move_hook_id) { try { gantt.detachEvent(gantt._hakiki_move_hook_id); } catch(_) {} }
    } catch(_) {}
    gantt._hakiki_move_hook_id = gantt.attachEvent('onAfterTaskMove', function(id, parent, tindex){
      try {
        const task = gantt.getTask(id);
        if (!task || task.nodeType !== 'task') return true;
        const key = String(id);
        // Skip if pending-new (not yet created on server)
        if (pendingNewTasksRef.current && pendingNewTasksRef.current.has(key)) {
          try { gantt.message({ type: 'info', text: 'Urutan akan disimpan setelah task dibuat.' }); } catch(_) {}
          return true;
        }
        // parentId must come from the task data column 'parent' (e.g., 'category_11')
        const payload = { parentId: (task.parent != null ? String(task.parent) : null), displayOrder: Number(tindex) };
        ganttService.updateTaskById(String(id), payload)
          .then(() => { try { gantt.message({ type: 'success', text: 'Urutan disimpan' }); } catch(_) {} })
          .catch(() => { try { gantt.message({ type: 'error', text: 'Gagal menyimpan urutan' }); } catch(_) {} try { refresh(); } catch(_) {} });
      } catch(_) {}
      return true;
    });

    // Intercept Save from popup: if it's a brand-new task, POST to /work-categories/{id}/tasks
    try {
      if (gantt._hakiki_save_hook_id) {
        try { gantt.detachEvent(gantt._hakiki_save_hook_id); } catch(_) {}
      }
    } catch(_) {}
    // Lightbox Cancel: if task is pending new, remove without PUT
    try {
      if (gantt._hakiki_cancel_hook_id) {
        try { gantt.detachEvent(gantt._hakiki_cancel_hook_id); } catch(_) {}
      }
    } catch(_) {}
    gantt._hakiki_cancel_hook_id = gantt.attachEvent('onLightboxCancel', function(id){
      try {
        const key = String(id);
        if (pendingNewTasksRef.current.has(key)) {
          pendingNewTasksRef.current.delete(key);
          try { gantt.deleteTask(id); } catch(_) {}
        }
      } catch(_) {}
      return true;
    });

    // Debounce mechanism for link operations
    let linkOperationTimeout = null;
    const pendingLinkOperations = new Set();

    // Handle interactive link creation (drag to create arrows)
    gantt.attachEvent('onAfterLinkAdd', function(id, link) {
      try {
        console.log('[Gantt] Link added:', link);
        
        // Prevent duplicate operations for the same link
        const linkKey = `${link.source}-${link.target}`;
        if (pendingLinkOperations.has(linkKey)) {
          console.log('[Gantt] Link operation already pending, skipping:', linkKey);
          return false;
        }
        
        pendingLinkOperations.add(linkKey);
        
        // Debounce the API call to prevent multiple requests
        if (linkOperationTimeout) {
          clearTimeout(linkOperationTimeout);
        }
        
        linkOperationTimeout = setTimeout(() => {
          try {
            // Find the target task and update its dependencies
            const targetTask = gantt.getTask(link.target);
            if (!targetTask) {
              pendingLinkOperations.delete(linkKey);
              return;
            }
            
            // Get existing dependencies from the raw data (more reliable source)
            const nodeFlat = (Array.isArray(window.__ganttFlatRaw) ? window.__ganttFlatRaw : []).find(n => String(n.id) === String(link.target));
            let dependencies = [];
            
            // First try to get from the flat node data
            if (nodeFlat && Array.isArray(nodeFlat.dependencies)) {
              dependencies = nodeFlat.dependencies.map(d => ({ 
                taskId: String(d.taskId ?? d.id ?? ''), 
                type: String(d.type || 'FS').toUpperCase(), 
                lag: Number(d.lag || 0) || 0 
              })).filter(d => d.taskId);
            }
            // Fallback to parsing pred field if no flat data
            else if (targetTask.pred) {
              const predStr = String(targetTask.pred);
              const predParts = predStr.split(',').filter(p => p.trim());
              
              predParts.forEach(part => {
                const m = part.trim().match(/^(\d+)(FS|FF|SS|SF)?(?::(-?\d+))?$/i);
                if (m) {
                  dependencies.push({ 
                    taskId: m[1], 
                    type: (m[2] || 'FS').toUpperCase(), 
                    lag: parseInt(m[3] || '0', 10) || 0 
                  });
                }
              });
            }
            
            // Check if this dependency already exists to avoid duplicates
            const sourceId = String(link.source);
            const existingDep = dependencies.find(d => d.taskId === sourceId);
            
            if (existingDep) {
              console.log('[Gantt] Dependency already exists, skipping:', existingDep);
              pendingLinkOperations.delete(linkKey);
              return;
            }
            
            // Add the new dependency
            let linkType = 'FS';
            if (link.type === '1') linkType = 'SS';
            else if (link.type === '2') linkType = 'FF';
            else if (link.type === '3') linkType = 'SF';
            
            dependencies.push({
              taskId: sourceId,
              type: linkType,
              lag: 0
            });
            
            console.log('[Gantt] Adding new dependency. Total dependencies:', dependencies);
            
            // Update the task's pred field
            const predString = dependencies.map(d => `${d.taskId}${d.type}:${d.lag}`).join(',');
            targetTask.pred = predString;
            
            // Save to backend (reuse nodeFlat from above)
            const pid = projectId || nodeFlat?.projectId || (typeof window !== 'undefined' && window.__currentProjectId);
            
            const payload = {
              dependencies: dependencies
            };
            
            ganttService.updateGanttTask(pid, link.target, payload)
              .then(() => {
                gantt.message({ type: 'success', text: 'Dependency added' });
                pendingLinkOperations.delete(linkKey);
                // Don't call refresh() immediately to prevent cascade of requests
                // try { refresh(); } catch (_) {}
              })
              .catch((err) => {
                console.error('Failed to save dependency:', err);
                gantt.message({ type: 'error', text: 'Failed to save dependency' });
                pendingLinkOperations.delete(linkKey);
                // Remove the link if save failed
                gantt.deleteLink(id);
              });
              
          } catch (e) {
            console.error('onAfterLinkAdd timeout error:', e);
            pendingLinkOperations.delete(linkKey);
          }
        }, 300); // 300ms debounce delay
          
      } catch (e) {
        console.error('onAfterLinkAdd error:', e);
      }
    });

    // Handle interactive link deletion (double-click to delete arrows)
    gantt.attachEvent('onAfterLinkDelete', function(id, link) {
      try {
        console.log('[Gantt] Link deleted:', link);
        
        // Prevent duplicate operations for the same link
        const linkKey = `${link.source}-${link.target}-delete`;
        if (pendingLinkOperations.has(linkKey)) {
          console.log('[Gantt] Link delete operation already pending, skipping:', linkKey);
          return false;
        }
        
        pendingLinkOperations.add(linkKey);
        
        // Debounce the API call to prevent multiple requests
        if (linkOperationTimeout) {
          clearTimeout(linkOperationTimeout);
        }
        
        linkOperationTimeout = setTimeout(() => {
          try {
            // Find the target task and update its dependencies
            const targetTask = gantt.getTask(link.target);
            if (!targetTask) {
              pendingLinkOperations.delete(linkKey);
              return;
            }
            
            // Get existing dependencies and remove the deleted one
            let dependencies = [];
            if (targetTask.pred) {
              const predStr = String(targetTask.pred);
              const predParts = predStr.split(',').filter(p => p.trim());
              
              predParts.forEach(part => {
                const m = part.trim().match(/^(\d+)(FS|FF|SS|SF)?(?::(-?\d+))?$/i);
                if (m && m[1] !== String(link.source)) { // Exclude the deleted link
                  dependencies.push({ 
                    taskId: m[1], 
                    type: (m[2] || 'FS').toUpperCase(), 
                    lag: parseInt(m[3] || '0', 10) || 0 
                  });
                }
              });
            }
            
            // Update the task's pred field
            const predString = dependencies.map(d => `${d.taskId}${d.type}:${d.lag}`).join(',');
            targetTask.pred = predString;
            
            // Save to backend
            const nodeFlat = (Array.isArray(window.__ganttFlatRaw) ? window.__ganttFlatRaw : []).find(n => String(n.id) === String(link.target));
            const pid = projectId || nodeFlat?.projectId || (typeof window !== 'undefined' && window.__currentProjectId);
            
            const payload = {
              dependencies: dependencies
            };
            
            ganttService.updateGanttTask(pid, link.target, payload)
              .then(() => {
                gantt.message({ type: 'success', text: 'Dependency removed' });
                pendingLinkOperations.delete(linkKey);
                // Don't call refresh() immediately to prevent cascade of requests
                // try { refresh(); } catch (_) {}
              })
              .catch((err) => {
                console.error('Failed to remove dependency:', err);
                gantt.message({ type: 'error', text: 'Failed to remove dependency' });
                pendingLinkOperations.delete(linkKey);
              });
              
          } catch (e) {
            console.error('onAfterLinkDelete timeout error:', e);
            pendingLinkOperations.delete(linkKey);
          }
        }, 300); // 300ms debounce delay
          
      } catch (e) {
        console.error('onAfterLinkDelete error:', e);
      }
    });

    // Lightbox Save: POST if pending new (detach existing to avoid duplicates)
    try {
      if (gantt._hakiki_save_hook_id) { 
        try { gantt.detachEvent(gantt._hakiki_save_hook_id); } catch(_) {} 
      }
    } catch(_) {}
    gantt._hakiki_save_hook_id = gantt.attachEvent('onLightboxSave', function(id, changes, is_new){
        try {
          const t = gantt.getTask(id);
          // debug
          const key = String(id);
          const inPending = pendingNewTasksRef.current.has(key);
          const shouldPost = !!is_new || inPending;
          try { console.log('[Gantt] NEW TASK onLightboxSave fired', { id, key, is_new, inPending, shouldPost }); } catch(_) {}
          if (!shouldPost) return true; // let existing update flow handle edits (PUT)

          // Resolve workCategoryId: prefer the one embedded on temp task, then captured, then fallback traversal
          let wcIdStr = null;
          try {
            if (t && t.workCategoryId != null && t.workCategoryId !== '') {
              wcIdStr = String(t.workCategoryId);
            }
          } catch(_) {}
          try {
            const preset = pendingNewTaskWCRef.current.get(key);
            if (preset != null && preset !== '') wcIdStr = String(preset);
          } catch(_) {}
          if (!wcIdStr) {
            // Fallback: Find nearest workCategory ancestor for the new task position
            wcIdStr = (function getWc(id){
              try {
                let cur = gantt.getTask(id);
                if (!cur) return null;
                // If the current task has workCategoryId, use it
                const direct = cur.workCategoryId ?? cur.work_category_id ?? cur.work_categoryId;
                if (direct != null && direct !== '') return String(direct);
                // If it is a workCategory node, try its own workCategoryId, else extract numeric part of id
                if (cur.nodeType === 'workCategory' || cur.type === 'workCategory') {
                  if (cur.workCategoryId != null && cur.workCategoryId !== '') return String(cur.workCategoryId);
                  const m = String(cur.id).match(/\d+/);
                  if (m) return m[0];
                }
                let p = cur.parent;
                while (p) {
                  const pt = gantt.getTask(p);
                  if (!pt) break;
                  const pDirect = pt.workCategoryId ?? pt.work_category_id ?? pt.work_categoryId;
                  if (pDirect != null && pDirect !== '') return String(pDirect);
                  if (pt.nodeType === 'workCategory' || pt.type === 'workCategory') {
                    if (pt.workCategoryId != null && pt.workCategoryId !== '') return String(pt.workCategoryId);
                    const m2 = String(pt.id).match(/\d+/);
                    if (m2) return m2[0];
                  }
                  p = pt.parent;
                }
                return null;
              } catch(_) { return null; }
            })(id);
          }
          try { console.log('[Gantt] resolved workCategoryId', { fromTask: t?.workCategoryId, fromPending: pendingNewTaskWCRef.current.get(key), final: wcIdStr }); } catch(_) {}

          if (!wcIdStr) {
            gantt.message({ type: 'warning', text: 'Pilih kategori pekerjaan terlebih dahulu' });
            return false;
          }

          // Normalize dates and duration
          const fmt = gantt.date.date_to_str('%Y-%m-%d');
          const toYMD = (d) => (d ? fmt(d) : null);
          const startDate = toYMD(t.start_date);
          const endDate = toYMD(t.end_date);
          let duration = Number(t.duration) || 1;
          if (startDate && endDate) {
            const s0 = new Date(startDate); s0.setHours(0,0,0,0);
            const e0 = new Date(endDate); e0.setHours(0,0,0,0);
            duration = Math.max(1, Math.round((e0 - s0) / (24*60*60*1000)) + 1);
          }

          // Parse predecessor field encoded in lightbox (e.g., "59FS:0")
          const deps = [];
          if (t.pred) {
            const m = String(t.pred).match(/^(\d+)(FS|FF|SS|SF)?(?::(-?\d+))?$/i);
            if (m) {
              deps.push({ taskId: m[1], type: (m[2]||'FS').toUpperCase(), lag: parseInt(m[3]||'0',10)||0 });
            }
          }

          const payload = {
            name: String(t.text || ''),
            startDate,
            endDate,
            duration,
            progress: Number(t.progress || 0),
            quantity: typeof t.quantity === 'number' ? t.quantity : (t.quantity ? Number(t.quantity) : 0),
            completedQuantity: 0,
            unit: t.unit || null,
            unitPrice: 0,
            autoCalculateWeight: true,
            weight: typeof t.weight === 'number' ? t.weight : (t.weight ? Number(t.weight) : 0),
            notes: t.notes || '',
            assignedTo: (t.assignedTo != null && t.assignedTo !== '') ? t.assignedTo : null,
            dependencies: deps,
            isMilestone: !!t.isMilestone,
            displayOrder: 0,
            masterJobId: t.masterJobId || null,
            jobCode: t.jobCode || '',
            status: t.status || 'to_do',
            workCategoryId: Number(wcIdStr),
          };

          // Async create then refresh and close popup; prevent default save
          (async () => {
            try {
              gantt.message({ type: 'info', text: 'Menyimpan tugas baru...' });
              await ganttService.createTask(wcIdStr, payload);
              gantt.message({ type: 'success', text: 'Task dibuat' });
              try { gantt.hideLightbox(); } catch(_) {}
              
              // Debounced refresh to prevent multiple calls
              if (window.__gantt_refresh_timeout) {
                clearTimeout(window.__gantt_refresh_timeout);
              }
              window.__gantt_refresh_timeout = setTimeout(() => {
                try { refresh(); } catch (_) {}
              }, 300);
              
              try { pendingNewTasksRef.current.delete(key); } catch(_) {}
              try { pendingNewTaskWCRef.current.delete(key); } catch(_) {}
            } catch (e) {
              gantt.message({ type: 'error', text: 'Gagal membuat task' });
              // eslint-disable-next-line no-console
              console.error('Create task failed', e, payload);
            }
          })();
          return false; // prevent default dhtmlx save for new task
        } catch(_) { return true; }
      });

    // When creating a new arrow, persist predecessor/dependency
    gantt.attachEvent('onBeforeLinkAdd', function(id, link){
      try {
        // Enforce single predecessor: remove any existing link targeting the same task
        const tgt = String(link?.target || '');
        if (!tgt) return true;
        const all = gantt.getLinks ? gantt.getLinks() : [];
        (all || []).forEach(l => {
          if (String(l.id) !== String(id) && String(l.target) === tgt) {
            try { gantt.deleteLink(l.id); } catch(_) {}
          }
        });
      } catch(_) {}
      return true;
    });

    gantt.attachEvent('onAfterLinkAdd', function(id, link){
      try {
        const source = String(link?.source || '');
        const target = String(link?.target || '');
        if (!source || !target) return;
        // Map dhtmlx type code to label
        let type = 'FS';
        const t = String(link?.type ?? '').trim();
        if (t === '1') type = 'SS';
        else if (t === '2') type = 'FF';
        else if (t === '3') type = 'SF';

        // Update target task model (UI only)
        const tt = gantt.getTask(target);
        if (tt) {
          // Do NOT touch assignedTo here (it represents installation team)
          tt.pred = `${source}${type}:0`;
        }
        const pid = (typeof projectId !== 'undefined' && projectId) ? projectId : (getFlatNode(target)?.projectId || (typeof window !== 'undefined' && window.__currentProjectId));
        // Build full payload similar to popup save and override predecessor info
        const payload = buildPayload(tt || { id: target });
        payload.dependencies = [{ taskId: source, type, lag: 0 }];

        ganttService.updateGanttTask(pid, target, payload)
          .then(() => {
            gantt.message({ type: 'success', text: 'Predecessor ditambahkan' });
            try { refresh(); } catch(_) {}
          })
          .catch(() => {
            // Revert link visually on failure
            try { gantt.deleteLink(id); } catch(_) {}
            gantt.message({ type: 'error', text: 'Gagal menyimpan predecessor' });
          });
      } catch(_) {}
    });

    // Delete predecessor by double-clicking on a link (arrow)
    gantt.attachEvent('onLinkDblClick', function(linkId, e){
      try {
        const link = gantt.getLink(linkId);
        if (!link) return false;
        const ok = window.confirm('Hapus hubungan (predecessor) ini?');
        if (!ok) return false;
        // Remove visually first for immediate feedback
        try { gantt.deleteLink(linkId); } catch(_) {}
        // Persist: clear predecessor on the successor (target) task
        const targetId = link.target;
        const t = gantt.getTask(targetId);
        if (!t) return true;
        t.pred = '';
        const pid = (typeof projectId !== 'undefined' && projectId) ? projectId : (getFlatNode(targetId)?.projectId || (typeof window !== 'undefined' && window.__currentProjectId));
        // Build payload and force predecessor-related fields to null/empty
        const payload = buildPayload(t);
        if (payload.dependencies) payload.dependencies = [];
        ganttService.updateGanttTask(pid, String(targetId), payload)
          .then(() => {
            gantt.message({ type: 'success', text: 'Predecessor dihapus' });
            try { refresh(); } catch(_) {}
          })
          .catch(() => {
            gantt.message({ type: 'error', text: 'Gagal menghapus predecessor' });
          });
      } catch(_) {}
      return false; // prevent default
    });

    // Force lightbox on double click for tasks, but intercept Actual bar
    gantt.attachEvent('onTaskDblClick', function(id, e){
      const t = gantt.getTask(id);
      const target = e && (e.target || e.srcElement);
      // If double-click on actual bar -> open actual dialog
      try {
        if (target && (target.classList?.contains('g-actual-bar') || target.closest?.('.g-actual-bar'))) {
          if (t?.nodeType === 'task') {
            // Open actual submissions list dialog
            try { if (openActualDialogRef.current) { openActualDialogRef.current(t); } } catch(_) {}
            return false;
          }
        }
      } catch(_) {}

      if (t?.nodeType === 'task') {
        // Adjust end_date to inclusive formula for the lightbox preview:
        try {
          const dayMs = 24*60*60*1000;
          const s = t.start_date ? new Date(t.start_date) : null;
          const dur = Math.max(1, Number(t.duration) || 1);
          if (s) {
            const eInc = new Date(s.getTime() + (dur - 1) * dayMs);
            eInc.setHours(23,59,59,0);
            t.end_date = eInc;
            gantt.updateTask(id);
          }
        } catch(_) {}
        gantt.showLightbox(id);
        return false; // prevent default select
      }
      return true;
    });

    // Enhance selects with Select2 when lightbox opens (if available)
    gantt.attachEvent('onLightbox', function(id) {
      console.log('[DEBUG] Lightbox opened for task ID:', id);
      lastLightboxTaskId.current = id;
      const tryInit = (attempt = 0) => {
        const box = document.querySelector('.gantt_cal_light');
        if (!box) return;
        // Add full-day policy hint under time section
        try {
          const sections = box.querySelectorAll('.gantt_cal_lsection');
          sections.forEach(sec => {
            const label = sec.querySelector('label');
            if (label && /Periode Waktu|Time|Duration/i.test(label.textContent || '')) {
              const container = sec.nextElementSibling; // .gantt_cal_ltext
              // Removed gtime-hint creation since we now have it built into the date picker
              // Update the native end-date preview (.gantt_duration_end_date) to: "Hari, s/d <date>"
              try {
                const task = gantt.getTask(id);
                const dayMs = 24*60*60*1000;
                const s = task.start_date ? new Date(task.start_date) : null;
                const dur = Math.max(1, Number(task.duration) || 1);
                if (s) {
                  const eInc = new Date(s.getTime() + (dur - 1) * dayMs);
                  const opts = { day: '2-digit', month: 'long', year: 'numeric' };
                  const endStr = eInc.toLocaleDateString('id-ID', opts);
                  const nativeEnd = container.querySelector('.gantt_duration_end_date');
                  if (nativeEnd) {
                    nativeEnd.textContent = `Hari, s/d ${endStr}`;
                  }
                }
              } catch(_) {}

              // Bind live updates when user changes day/month/year or duration (+/-)
              try {
                if (!container.dataset.timeBind) {
                  const endElGlobal = box.querySelector('.gantt_duration_end_date');
                  const durInput = container.querySelector('.gantt_duration_value, input[name="duration"], input[type="number"]');
                  const selects = container.querySelectorAll('select');
                  // Try common button classnames used by dhtmlx skins
                  const minusBtn = container.querySelector('.gantt_duration .minus, .gantt_duration_dec, .gantt_minus');
                  const plusBtn = container.querySelector('.gantt_duration .plus, .gantt_duration_inc, .gantt_plus');
                  const computeAndRender = () => {
                    const endEl = box.querySelector('.gantt_duration_end_date') || endElGlobal;
                    if (!endEl) return;
                    // try read selects as [day, month, year]
                    let day = null, month = null, year = null;
                    const arr = Array.from(selects);
                    if (arr.length >= 3) {
                      day = parseInt(arr[0].value, 10);
                      month = arr[1].selectedIndex; // month index 0..11
                      year = parseInt(arr[2].value, 10);
                    }
                    if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) return;
                    const start = new Date(year, month, day, 0, 0, 1, 0);
                    let dVal = null;
                    if (durInput) {
                      // try value, else textContent
                      const raw = durInput.value != null ? durInput.value : durInput.textContent;
                      dVal = parseInt(raw, 10);
                    }
                    if (!isFinite(dVal)) {
                      try { const t = gantt.getTask(id); dVal = parseInt(t?.duration, 10); } catch(_) {}
                    }
                    let d = Math.max(1, isFinite(dVal) ? dVal : 1);
                    const eInc = new Date(start.getTime() + (d - 1) * 24*60*60*1000);
                    const endStr = eInc.toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' });
                    endEl.textContent = `Hari, s/d ${endStr}`;
                  };
                  // Delegated handlers to capture any dynamic control updates
                  selects.forEach(sel => sel.addEventListener('change', computeAndRender));
                  if (durInput) {
                    durInput.addEventListener('input', computeAndRender);
                    durInput.addEventListener('change', computeAndRender);
                  }
                  if (minusBtn) minusBtn.addEventListener('click', () => setTimeout(computeAndRender, 0));
                  if (plusBtn) plusBtn.addEventListener('click', () => setTimeout(computeAndRender, 0));
                  // Also listen on container for any click/input bubbling from +/- rendered differently
                  container.addEventListener('click', () => setTimeout(computeAndRender, 0), true);
                  container.addEventListener('input', computeAndRender, true);
                  // Observe DOM mutations in case the library re-renders duration value
                  try {
                    const mo = new MutationObserver(() => setTimeout(computeAndRender, 0));
                    mo.observe(container, { subtree: true, childList: true, characterData: true, attributes: true });
                    container.__mo = mo;
                  } catch(_) {}
                  container.dataset.timeBind = '1';
                  // initial compute
                  setTimeout(computeAndRender, 0);
                  // Polling fallback to catch any internal updates we miss
                  try {
                    window.__g_time_container = container;
                    if (!window.__g_time_poll) {
                      window.__g_time_poll = setInterval(computeAndRender, 200);
                    }
                  } catch(_) {}
                }
              } catch(_) {}
            }
          });
        } catch(_) {}
        const allSelects = box.querySelectorAll('select');
        // Exclude selects inside the time section from Select2
        const selects = Array.from(allSelects).filter(el => !el.closest('.gantt_section_time'));
        // Find subcontractor select (prefer name attribute from map_to)
        const task = gantt.getTask(id);
        let subSel = box.querySelector('select[name="assignedTo"]');
        if (!subSel) {
          const sections = box.querySelectorAll('.gantt_cal_lsection');
          sections.forEach(sec => {
            const label = sec.querySelector('label');
            if (label && /Subcontractor/i.test(label.textContent || '')) {
              const container = sec.nextElementSibling; // .gantt_cal_ltext
              const sel = container ? container.querySelector('select') : null;
              if (sel) subSel = sel;
            }
          });
        }
        // If options not populated yet, inject from latest teams
        try {
          if (subSel && subSel.options && subSel.options.length <= 1) {
            // clear and add placeholder + teams
            subSel.innerHTML = '';
            const placeholder = document.createElement('option');
            placeholder.value = '';
            placeholder.textContent = 'Select Subcontractor';
            subSel.appendChild(placeholder);
            const opts = (teams || []).map(t => ({
              key: String(t.id ?? ''),
              label: t.name ?? ''
            }));
            opts.forEach(o => {
              const opt = document.createElement('option');
              opt.value = o.key;
              opt.textContent = o.label;
              subSel.appendChild(opt);
            });
            if (subSel.options.length <= 1) {
              gantt.message({ type: 'warning', text: 'Subcontractor list is empty.' });
            }
          }
        } catch (_) {}
        if (window.$ && window.$.fn && window.$.fn.select2) {
          // Initialize or refresh Select2
          window.$(selects).select2({ width: '100%' });
          if (subSel) {
            window.$(subSel).trigger('change.select2');
          }
          if (subSel && task) {
            const val = task.assignedTo != null ? String(task.assignedTo) : '';
            window.$(subSel).val(val).trigger('change.select2');
          }
        } else if (attempt < 20) {
          setTimeout(() => tryInit(attempt + 1), 100);
        }
      };
      setTimeout(() => tryInit(0), 0);
      return true;
    });

    // Removed grid Split handler (moved to lightbox button)

    return () => {
      try {
        if (gantt && gantt.$root) {
          gantt.clearAll();
        }
        // detach fallback split DOM handler
        try {
          const root = gantt.$root || (containerEl ? containerEl : null);
          const grid = root ? root.querySelector('.gantt_grid_data') : null;
          if (grid && grid.__splitHandler) {
            grid.removeEventListener('click', grid.__splitHandler, true);
            delete grid.__splitHandler;
          }
        } catch(_) {}
      } catch (e) {
        // ignore teardown errors (can happen during hot-reload)
      }
    };
  }, [raw, projectId, refresh, teams]);

  // Load/refresh data when dataset changes
  useEffect(() => {
    // Update subcontractor options in lightbox
    const subcontractorOptions = (teams || []).map(t => ({
      key: String(t.id ?? ''),
      label: t.name ?? ''
    }));
    
    // Find and update subcontractor section
    const sections = gantt.config.lightbox.sections;
    const subSection = sections.find(s => s.name === 'subcontractor');
    if (subSection) {
      subSection.options = [{ key: '', label: 'Select Subcontractor' }, ...subcontractorOptions];
      // Rebuild lightbox to reflect new options
      try { gantt.resetLightbox(); } catch (_) {}
    }

    // Build predecessors string for grid column
    const predMap = new Map();
    (raw || []).forEach((n) => {
      if (!Array.isArray(n.dependencies)) return;
      const s = n.dependencies.map((d) => `${d.taskId ?? d.id ?? ''}${(d.type || '').toUpperCase()}`).join(',');
      predMap.set(String(n.id), s);
    });

    // Build dataset with child "Progress Actual" rows
    const builtRows = [];
    (dataset.data || []).forEach((t) => {
      // Base row
      let status = 'todo';
      const prog = Number(t.progress || 0);
      if (prog >= 1) status = 'done';
      else if (prog > 0) status = 'inprogress';
      builtRows.push({ ...t, pred: predMap.get(String(t.id)) || '', status });

      // Actual child row spanning from earliest to latest Work Date (only when showActual is true)
      try {
        if (showActual && t.nodeType === 'task' && hasApprovedForTask(String(t.id))) {
          const dates = typeof getWorkDatesForTask === 'function' ? getWorkDatesForTask(t.id) : [];
          if (Array.isArray(dates) && dates.length) {
            // Compute min/max
            const valid = dates.filter(x => x && !isNaN(new Date(x))).map(x => new Date(x));
            if (valid.length) {
              valid.sort((a,b) => a - b);
              const dStart = new Date(valid[0]);
              const dEnd = new Date(valid[valid.length - 1]);
              // Build local Date objects at 00:00:01 and 23:59:59 to avoid UTC shift
              const s = new Date(dStart.getFullYear(), dStart.getMonth(), dStart.getDate(), 0, 0, 1, 0);
              const e = new Date(dEnd.getFullYear(), dEnd.getMonth(), dEnd.getDate(), 23, 59, 59, 0);
              // Inclusive duration in days
              const s0 = new Date(s); s0.setHours(0,0,0,0);
              const e0 = new Date(e); e0.setHours(0,0,0,0);
              const dayMs = 24*60*60*1000;
              const dur = Math.max(1, Math.round((e0 - s0) / dayMs) + 1);
              // Compute total actual volume and progress ratio vs planned quantity
              let totalActual = 0;
              try { totalActual = Number(getTotalVolumeForTask ? getTotalVolumeForTask(t.id) : 0) || 0; } catch(_) {}
              const plannedQty = Number(t.quantity) || 0;
              const ratio = plannedQty > 0 ? Math.max(0, Math.min(1, totalActual / plannedQty)) : 0;
              builtRows.push({
                id: `${t.id}__actual_span`,
                text: 'Progress Actual',
                parent: String(t.id),
                start_date: s,
                end_date: e,
                duration: dur,
                progress: ratio,
                nodeType: 'actual',
                open: true,
                actualTotalVolume: totalActual,
                unit: t.unit || null,
                $readonly: true,
                $no_link: true,
                $source: false,
                $target: false,
              });
            }
          }
        }
      } catch(_) {}
    });

    const enriched = { data: builtRows, links: dataset.links || [] };

    // Debug: Log links to verify multiple predecessors are being generated
    if (enriched.links && enriched.links.length > 0) {
      console.log('[Gantt] Generated links for multiple predecessors:', enriched.links);
    }

    try {
      if (gantt && gantt.$root) {
        gantt.clearAll();
      }
      gantt.parse(enriched);
      
      // Force refresh of links to ensure multiple predecessors are displayed
      if (gantt.refreshData) {
        gantt.refreshData();
      }
      
      // Initialize timeline drag scrolling after render
      setTimeout(() => {
        initTimelineDrag();
      }, 100);
    } catch (e) {
      // Safeguard against transient states (e.g., hot-reload)
      // Re-init if necessary
      if (ganttRef.current && !gantt.$root) {
        try { gantt.init(ganttRef.current); } catch (_) {}
        try { gantt.parse(enriched); } catch (_) {}
        try { gantt.refreshData(); } catch (_) {}
        try { setTimeout(() => initTimelineDrag(), 100); } catch (_) {}
      }
    }
    // Compute container height to fit all VISIBLE rows (after filters), avoid big blank spaces
    // We mirror the same logic as in grid_row_class (search + unit filter), because
    // gantt.isTaskVisible may not reflect template-based CSS hiding.
    const computeVisible = () => {
      try {
        if (!(gantt && typeof gantt.eachTask === 'function')) {
          return (enriched.data || []).length;
        }
        let count = 0;
        const q = searchText ? String(searchText).toLowerCase() : '';
        const hasUnit = !!unitFilterId;
        const selectedUnitIdStr = hasUnit ? String(unitFilterId) : null;
        const belongsToSelectedUnit = (task) => {
          if (!hasUnit) return true;
          // Only root project is exempt; modularUnit must be filtered by selection
          if (task.nodeType === 'project') return true;
          let currentId = task.id;
          while (currentId) {
            const currentTask = gantt.getTask(currentId);
            if (!currentTask) break;
            if (currentTask.nodeType === 'modularUnit' || currentTask.type === 'modularUnit') {
              const unitIdMatch = String(currentTask.id).match(/\d+$/);
              return !!(unitIdMatch && unitIdMatch[0] === selectedUnitIdStr);
            }
            currentId = currentTask.parent;
          }
          return false;
        };
        gantt.eachTask((task) => {
          try {
            let visible = true;
            // Respect branch open/closed state
            if (typeof gantt.isTaskVisible === 'function' && !gantt.isTaskVisible(task.id)) {
              visible = false;
            }
            if (q) {
              if (task.nodeType === 'task' && !String(task.text || '').toLowerCase().includes(q)) {
                visible = false;
              }
            }
            if (visible && hasUnit) {
              visible = belongsToSelectedUnit(task);
            }
            if (visible) count++;
          } catch(_) {}
        });
        return count;
      } catch(_) {
        return (enriched.data || []).length;
      }
    };
    const visibleRows = computeVisible();
    const rowH = gantt.config.row_height || 28;
    const scalesH = gantt.config.scale_height || 64;
    const headerH = 36; // grid header approx
    const padding = 24; // small padding
    setHeight(Math.max(260, visibleRows * rowH + scalesH + headerH + padding));
    // expose latest flat raw for lightbox blocks
    try { window.__ganttFlatRaw = raw; } catch (_) {}
    // Ensure layout recalculates after height/filters
    try { setTimeout(() => { try { gantt.setSizes(); } catch(_) {} }, 0); } catch(_) {}
  }, [dataset, teams, hasApprovedForTask, getWorkDatesForTask, getTotalVolumeForTask, initTimelineDrag, raw, refresh, showActual, searchText, unitFilterId]);

  // Combined filter for Live Search dan Unit/Building menggunakan onBeforeTaskDisplay (tanpa CSS bolong)
  useEffect(() => {
    const q = searchText ? String(searchText).toLowerCase() : '';
    const hasUnit = unitFilterId != null && unitFilterId !== '';
    const selectedUnitIdStr = hasUnit ? String(unitFilterId) : null;

    const belongsToSelectedUnit = (task) => {
      if (!hasUnit) return true;
      // Only root project is exempt; modularUnit must be filtered by selection
      if (task.nodeType === 'project') return true;
      let currentId = task.id;
      while (currentId) {
        const currentTask = gantt.getTask(currentId);
        if (!currentTask) break;
        if (currentTask.nodeType === 'modularUnit' || currentTask.type === 'modularUnit') {
          const unitIdMatch = String(currentTask.id).match(/\d+$/);
          return !!(unitIdMatch && unitIdMatch[0] === selectedUnitIdStr);
        }
        currentId = currentTask.parent;
      }
      return false;
    };

    const key = gantt.attachEvent('onBeforeTaskDisplay', function(id, task) {
      try {
        // 1) Search filter: only apply to task rows (not folders)
        if (q) {
          if (task.nodeType === 'task' && !String(task.text || '').toLowerCase().includes(q)) {
            return false;
          }
        }
        // 2) Unit filter
        if (hasUnit) {
          if (!belongsToSelectedUnit(task)) return false;
        }
        return true;
      } catch(_) { return true; }
    });

    try { gantt.refreshData(); } catch(_) {}

    return () => {
      try { gantt.detachEvent(key); } catch(_) {}
    };
  }, [searchText, unitFilterId]);

  // If teams arrive while lightbox is open, inject options and set default
  useEffect(() => {
    const id = lastLightboxTaskId.current;
    if (!id) return;
    const box = document.querySelector('.gantt_cal_light');
    if (!box) return;
    let subSel = null;
    const sections = box.querySelectorAll('.gantt_cal_lsection');
    sections.forEach(sec => {
      const label = sec.querySelector('label');
      if (label && /Subcontractor/i.test(label.textContent || '')) {
        const container = sec.nextElementSibling;
        const sel = container ? container.querySelector('select') : null;
        if (sel) subSel = sel;
      }
    });
    if (!subSel) return;
    // Refill options
    subSel.innerHTML = '';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Select Subcontractor';
    subSel.appendChild(placeholder);
    const opts = (teams || []).map(t => ({
      key: String(t.id ?? t.teamId ?? t.ID ?? ''),
      label: t.name ?? t.teamName ?? t.displayName ?? `Team ${t.id}`
    }));
    opts.forEach(o => {
      const opt = document.createElement('option');
      opt.value = o.key;
      opt.textContent = o.label;
      subSel.appendChild(opt);
    });
    // Set default from task.assignedTo
    const task = gantt.getTask(id);
    const val = task?.assignedTo != null ? String(task.assignedTo) : '';
    if (window.$ && window.$.fn && window.$.fn.select2) {
      window.$(subSel).val(val).trigger('change');
    } else {
      subSel.value = val;
    }
  }, [teams]);

  // Expose control methods to parent toolbar
  useEffect(() => {
    if (!onReady) return;
    const api = {
      collapseAll: () => gantt.batchUpdate(() => gantt.eachTask(t => gantt.close(t.id))),
      expandAll: () => gantt.batchUpdate(() => gantt.eachTask(t => gantt.open(t.id))),
      collapseToProject: () => {
        gantt.batchUpdate(() => {
          gantt.eachTask(t => {
            if (t.nodeType === 'workCategory' || t.nodeType === 'modularUnit') {
              gantt.close(t.id);
            } else if (t.nodeType === 'project') {
              gantt.open(t.id);
            }
          });
        });
      },
      collapseToCategory: () => {
        gantt.batchUpdate(() => {
          gantt.eachTask(t => {
            if (t.nodeType === 'workCategory') {
              gantt.close(t.id);
            } else if (t.nodeType === 'project' || t.nodeType === 'modularUnit') {
              gantt.open(t.id);
            }
          });
        });
      },
      refresh: () => refresh(),
      addTask: handleOpenAddTaskDialog,
      setSearch: (s) => { /* handled via prop */ },
      // Export full Gantt (grid + timeline) to PNG - capture current on-screen state without extra filters
      executeExportInternal: async () => {
        try { gantt.message({ type: 'info', text: 'Menyiapkan export gambar...' }); } catch(_) {}
        // No temporary display filters are applied here; export follows the current UI filters/state.
        
        // Count total tasks to determine export strategy
        let taskCount = 0;
        try {
          gantt.eachTask(function(task) {
            if (!task) return; // Safety check
            try {
              if (gantt.isTaskVisible(task.id)) {
                taskCount++;
              }
            } catch(_) {}
          });
        } catch(_) { taskCount = 0; }
        
        console.log(`[Export] Total tasks: ${taskCount}`);
        
        // For very large datasets (>500 tasks), use optimized approach
        const isLargeDataset = taskCount > 500;
        try {
          // Try built-in dhtmlx export if available (requires export module/server)
          if (gantt.exportToPNG) {
            // Ensure all branches are open to avoid truncated content
            const opened = [];
            gantt.eachTask(t => { if (!gantt.isBranchOpen(t.id)) { opened.push(t.id); gantt.open(t.id); } });
            // Export full chart. Some builds accept config, keep minimal for compatibility
            gantt.exportToPNG({ raw: false });
            try { gantt.message({ type: 'success', text: 'Export PNG dimulai. Jika tidak ada file, periksa popup blocker.' }); } catch(_) {}
            // Optionally restore state (safe no-op)
            opened.forEach(id => { try { gantt.close(id); } catch(_) {} });
            return;
          }
        } catch (_) { /* fall back */ }

        // Fallback: capture container using dom-to-image (if included elsewhere)
        try {
          // Show enhanced loading overlay for large datasets
          let overlay = document.getElementById('gantt-export-overlay');
          if (!overlay) {
            overlay = document.createElement('div');
            overlay.id = 'gantt-export-overlay';
            overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.25);z-index:9999;display:flex;align-items:center;justify-content:center;';
            const loadingText = isLargeDataset ? `Mengekspor ${taskCount} task, mohon tunggu (mungkin memakan waktu beberapa menit)...` : 'Mengekspor gambar, mohon tunggu...';
            overlay.innerHTML = `<div style="background:#fff;padding:14px 18px;border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,0.2);display:flex;gap:10px;align-items:center;font-family:sans-serif;">\n  <span class="spinner" style="width:16px;height:16px;border:2px solid #1976d2;border-top-color:transparent;border-radius:50%;display:inline-block;animation:spin 0.8s linear infinite"></span>\n  <span>${loadingText}</span>\n</div>`;
            const spinCss = document.createElement('style');
            spinCss.id = 'gantt-export-overlay-style';
            spinCss.textContent = '@keyframes spin{to{transform:rotate(360deg)}}';
            document.head.appendChild(spinCss);
            document.body.appendChild(overlay);
          }
          const container = ganttRef.current || document.querySelector('#gantt_here, .gantt_container, .gantt_layout');
          if (!container) throw new Error('Gantt container not found');
          
          // Validate container dimensions
          const containerRect = container.getBoundingClientRect();
          console.log('[Export] Container dimensions:', { 
            width: containerRect.width, 
            height: containerRect.height,
            scrollWidth: container.scrollWidth,
            scrollHeight: container.scrollHeight
          });
          
          if (containerRect.width === 0 || containerRect.height === 0) {
            throw new Error('Container has zero dimensions - cannot export');
          }
          // Temporarily expand to full height and width to avoid right-side truncation
          const layout = container.querySelector('.gantt_layout') || container;
          const taskArea = container.querySelector('.gantt_task_area') || container.querySelector('.gantt_timeline_area') || container.querySelector('.gantt_data_area');
          const gridArea = container.querySelector('.gantt_grid') || container.querySelector('.gantt_grid_scale');
          const prevCont = { h: container.style.height, w: container.style.width, ov: container.style.overflow };
          const prevLayout = layout ? { w: layout.style.width, ov: layout.style.overflow } : {};
          const prevTask = taskArea ? { w: taskArea.style.width, ov: taskArea.style.overflow } : {};
          // Compute required width to include full timeline to the rightmost date
          let requiredWidth = Math.max(container.scrollWidth, layout?.scrollWidth || 0, taskArea?.scrollWidth || 0);
          try {
            const state = gantt.getState ? gantt.getState() : null;
            if (state && gantt.posFromDate) {
              const maxPos = gantt.posFromDate(state.max_date); // px width of timeline
              const gridW = (gridArea && gridArea.offsetWidth) ? gridArea.offsetWidth : 0;
              // Add 60px padding to avoid clipping the last day label
              requiredWidth = Math.max(requiredWidth, gridW + maxPos + 60);
            }
          } catch(_) {}
          try {
            container.style.height = 'auto';
            if (requiredWidth && Number.isFinite(requiredWidth)) {
              container.style.width = `${requiredWidth}px`;
              if (layout) layout.style.width = `${requiredWidth}px`;
              if (taskArea) taskArea.style.width = `${requiredWidth}px`;
            }
            container.style.overflow = 'visible';
            if (layout) layout.style.overflow = 'visible';
            if (taskArea) taskArea.style.overflow = 'visible';
            gantt.render();
          } catch(_) {}

          // Temporarily increase row/bar height and column widths for better readability
          const prevRowHeight = gantt.config.row_height;
          const prevBarHeight = gantt.config.bar_height;
          const prevColWidths = [];
          if (Array.isArray(gantt.config.columns)) {
            gantt.config.columns.forEach((col, idx) => {
              prevColWidths[idx] = col.width;
            });
          }
          try {
            if (Array.isArray(gantt.config.columns)) {
              // Optimize column widths based on dataset size
              gantt.config.columns.forEach((col, idx) => {
                if (isLargeDataset) {
                  // Smaller widths for large datasets to reduce memory usage
                  if (idx === 0) col.width = 800; // Task name - reduced for large datasets
                  else if (col.name === 'start_date' || col.name === 'end_date') col.width = 130;
                  else if (col.name === 'duration') col.width = 100;
                  else if (col.name === 'weight') col.width = 100;
                  else if (col.name === 'volume') col.width = 130;
                  else if (col.name === 'progress') col.width = 110;
                  else if (col.name === 'predecessors') col.width = 160;
                  else col.width = Math.max(col.width || 100, 130);
                } else {
                  // Standard widths for smaller datasets
                  if (idx === 0) col.width = 1200;
                  else if (col.name === 'start_date' || col.name === 'end_date') col.width = 170;
                  else if (col.name === 'duration') col.width = 150;
                  else if (col.name === 'weight') col.width = 150;
                  else if (col.name === 'volume') col.width = 150;
                  else if (col.name === 'progress') col.width = 150;
                  else if (col.name === 'predecessors') col.width = 180;
                  else col.width = Math.max(col.width || 100, 120);
                }
              });
            }
            // Optimize row heights for large datasets
            gantt.config.row_height = isLargeDataset ? 36 : 55;
            gantt.config.bar_height = isLargeDataset ? 18 : 25;
            gantt.config.show_task_cells = false;
            gantt.render();
            // Longer wait for large datasets
            const waitTime = isLargeDataset ? 1500 : 500;
            await new Promise(resolve => setTimeout(resolve, waitTime));
          } catch(_) {}

          // Inject export-specific styles optimized for dataset size
          let exportStyleEl = document.getElementById('gantt-export-style');
          const baseFontSize = isLargeDataset ? '10px' : '11px'; // Increase font size for better clarity
          const basePadding = isLargeDataset ? '3px 5px' : '4px 6px'; // Increase padding for better readability
          const baseLineHeight = isLargeDataset ? '1.5' : '1.6';
          
          const exportCss = `
            /* improve readability for all grid cells with proper spacing and anti-aliasing */
            .gantt_container .gantt_grid_data .gantt_cell { 
              white-space: normal !important; 
              font-size: ${baseFontSize} !important; 
              font-family: Arial, Helvetica, sans-serif !important;
              font-weight: 500 !important; /* Slightly bolder for better clarity */
              padding: ${basePadding} !important;
              overflow: visible !important;
              box-sizing: border-box !important;
              text-align: left !important;
              vertical-align: top !important;
              line-height: ${baseLineHeight} !important;
              word-wrap: break-word !important;
              letter-spacing: ${isLargeDataset ? '0.3px' : '0.5px'} !important;
              word-spacing: ${isLargeDataset ? '1px' : '2px'} !important;
              /* Enhanced text rendering */
              -webkit-font-smoothing: antialiased !important;
              -moz-osx-font-smoothing: grayscale !important;
              text-rendering: optimizeLegibility !important;
            }
            .gantt_container .gantt_grid_scale .gantt_grid_head_cell { 
              white-space: nowrap !important; 
              font-size: 11px !important; 
              font-family: Arial, Helvetica, sans-serif !important;
              font-weight: bold !important;
              padding: 3px 5px !important;
              box-sizing: border-box !important;
              text-align: center !important;
              vertical-align: middle !important;
              background-color: #f5f5f5 !important;
              border: 1px solid #ddd !important;
              /* Enhanced text rendering for headers */
              -webkit-font-smoothing: antialiased !important;
              -moz-osx-font-smoothing: grayscale !important;
              text-rendering: optimizeLegibility !important;
            }
            /* task name column specific styling for better text wrapping */
            .gantt_container .gantt_tree_content {
              white-space: normal !important;
              overflow: visible !important;
              font-size: ${baseFontSize} !important;
              font-family: Arial, Helvetica, sans-serif !important;
              font-weight: 500 !important; /* Slightly bolder for task names */
              line-height: ${baseLineHeight} !important;
              word-wrap: break-word !important;
              word-break: break-word !important;
              max-width: ${isLargeDataset ? '780px' : '1180px'} !important;
              display: block !important;
              padding: ${isLargeDataset ? '2px 6px 2px 0' : '4px 8px 4px 0'} !important;
              letter-spacing: ${isLargeDataset ? '0.3px' : '0.5px'} !important;
              word-spacing: ${isLargeDataset ? '1px' : '2px'} !important;
              /* Enhanced text rendering for task names */
              -webkit-font-smoothing: antialiased !important;
              -moz-osx-font-smoothing: grayscale !important;
              text-rendering: optimizeLegibility !important;
            }
            /* optimal row height for readability */
            .gantt_container .gantt_row { 
              height: ${isLargeDataset ? '36px' : '55px'} !important; 
              min-height: ${isLargeDataset ? '36px' : '55px'} !important;
            }
            .gantt_container .gantt_grid_data .gantt_cell { 
              height: ${isLargeDataset ? '36px' : '55px'} !important; 
              min-height: ${isLargeDataset ? '36px' : '55px'} !important;
            }
            .gantt_container .gantt_task_row {
              height: ${isLargeDataset ? '36px' : '55px'} !important;
              min-height: ${isLargeDataset ? '36px' : '55px'} !important;
            }
            .gantt_container .gantt_task_cell {
              height: ${isLargeDataset ? '36px' : '55px'} !important;
              min-height: ${isLargeDataset ? '36px' : '55px'} !important;
            }
            /* show task names on gantt bars for export */
            .gantt_container .gantt_task_text,
            .gantt_container .gantt_bar_task .gantt_task_content,
            .gantt_container .gantt_task_content,
            .gantt_container .gantt_task_line .gantt_task_text,
            .gantt_container .gantt_task_line .gantt_task_content {
              display: block !important;
              color: #000 !important;
              font-size: 9px !important;
              line-height: 1.35 !important;
              font-family: Arial, Helvetica, sans-serif !important;
              font-weight: 700 !important;
              letter-spacing: 0.6px !important;
              word-spacing: 1.2px !important;
              padding: 0 8px !important;
              text-align: left !important;
              position: relative !important;
              z-index: 5 !important;
              overflow: visible !important;
              white-space: nowrap !important;
              text-overflow: clip !important;
            }
            /* override for Actual bars: keep their text hidden in export */
            .gantt_container .gantt_task_line.bar-actual .gantt_task_text,
            .gantt_container .gantt_task_line.bar-actual .gantt_task_content {
              display: none !important;
            }
            /* ensure bar container allows text to overflow visibly */
            .gantt_container .gantt_task_line { overflow: visible !important; }
            /* Hide progress text overlay */
            .gantt_container .gantt_task_progress_text {
              display: none !important;
            }
            /* smaller font for timeline headers */
            .gantt_container .gantt_scale_cell {
              font-size: 9px !important;
              font-family: Arial, Helvetica, sans-serif !important;
            }
            .gantt_container .gantt_task_scale .gantt_scale_cell {
              font-size: 9px !important;
              font-family: Arial, Helvetica, sans-serif !important;
            }
            /* ensure proper grid layout */
            .gantt_container .gantt_grid_data { 
              overflow: visible !important; 
            }
            /* add borders for better readability */
            .gantt_container .gantt_grid_data .gantt_cell {
              border-right: 1px solid #e0e0e0 !important;
              border-bottom: 1px solid #e0e0e0 !important;
            }
          `;
          if (!exportStyleEl) {
            exportStyleEl = document.createElement('style');
            exportStyleEl.id = 'gantt-export-style';
            exportStyleEl.type = 'text/css';
            exportStyleEl.appendChild(document.createTextNode(exportCss));
            document.head.appendChild(exportStyleEl);
          }

          // Build filename with task count for large datasets
          const getProjectName = () => {
            try {
              const arr = Array.isArray(window.__ganttFlatRaw) ? window.__ganttFlatRaw : [];
              const proj = arr.find(n => (String(n.type || '').toLowerCase() === 'project') || n.isProject);
              const nm = proj?.name || proj?.text;
              if (nm) return String(nm).trim();
            } catch(_) {}
            return 'Gantt';
          };
          const pad2 = (n) => String(n).padStart(2, '0');
          const now = new Date();
          const taskSuffix = isLargeDataset ? ` (${taskCount} tasks)` : '';
          const fname = `${getProjectName()}${taskSuffix} - ${pad2(now.getDate())}-${pad2(now.getMonth()+1)}-${now.getFullYear()} ${pad2(now.getHours())}.${pad2(now.getMinutes())}`;
          
          // Enhanced export options for large datasets
          const exportOptions = {
            bgcolor: '#ffffff',
            quality: isLargeDataset ? 0.8 : 1.0, // Reduce quality for large datasets
            cacheBust: true,
            imagePlaceholder: undefined,
            pixelRatio: isLargeDataset ? 1 : 2 // Lower pixel ratio for large datasets
          };
          
          if (window.domtoimage && window.domtoimage.toPng) {
            try {
              console.log('[Export] Attempting dom-to-image with options:', exportOptions);
              let dataUrl = await window.domtoimage.toPng(container, exportOptions);
              console.log('[Export] dom-to-image data URL length:', dataUrl?.length || 0);
              
              if (!dataUrl || dataUrl === 'data:,' || dataUrl.length < 100) {
                console.warn('[Export] dom-to-image produced empty/invalid data URL, trying fallback options');
                // Try with reduced options
                const fallbackOptions = {
                  bgcolor: '#ffffff',
                  quality: 0.7,
                  cacheBust: false,
                  pixelRatio: 1
                };
                const fallbackDataUrl = await window.domtoimage.toPng(container, fallbackOptions);
                if (!fallbackDataUrl || fallbackDataUrl === 'data:,' || fallbackDataUrl.length < 100) {
                  throw new Error('dom-to-image fallback also failed');
                }
                dataUrl = fallbackDataUrl;
              }
              
              const a = document.createElement('a');
              a.href = dataUrl; a.download = `${fname}.png`; a.click();
              try { gantt.message({ type: 'success', text: 'Export PNG berhasil diunduh.' }); } catch(_) {}
              // Remove overlay immediately after download
              const ov = document.getElementById('gantt-export-overlay');
              if (ov && ov.parentNode) ov.parentNode.removeChild(ov);
            } catch (domErr) {
              console.warn('dom-to-image failed:', domErr);
              throw domErr; // Fall through to next method
            }
          } else {
            // Try to load dom-to-image on-demand, then retry once
            await new Promise((resolve) => {
              const id = 'dom-to-image-more-cdn';
              if (document.getElementById(id)) return resolve();
              const s = document.createElement('script');
              s.id = id; s.async = true;
              s.onload = resolve;
              s.src = 'https://cdn.jsdelivr.net/npm/dom-to-image-more@3.3.6/dist/dom-to-image-more.min.js';
              document.body.appendChild(s);
            });
            if (window.domtoimage && window.domtoimage.toPng) {
              try {
                console.log('[Export] Retrying dom-to-image with options:', exportOptions);
                let dataUrl = await window.domtoimage.toPng(container, exportOptions);
                console.log('[Export] dom-to-image retry data URL length:', dataUrl?.length || 0);
                
                if (!dataUrl || dataUrl === 'data:,' || dataUrl.length < 100) {
                  console.warn('[Export] dom-to-image retry also produced empty/invalid data URL');
                  // Try with minimal options on retry
                  const minimalOptions = {
                    bgcolor: '#ffffff',
                    quality: 0.5,
                    cacheBust: false,
                    pixelRatio: 1
                  };
                  const minimalDataUrl = await window.domtoimage.toPng(container, minimalOptions);
                  if (!minimalDataUrl || minimalDataUrl === 'data:,' || minimalDataUrl.length < 100) {
                    throw new Error('dom-to-image retry with minimal options also failed');
                  }
                  dataUrl = minimalDataUrl;
                }
                
                const a = document.createElement('a');
                a.href = dataUrl; a.download = `${fname}.png`; a.click();
                try { gantt.message({ type: 'success', text: 'Export PNG berhasil diunduh.' }); } catch(_) {}
                // Remove overlay immediately after download
                const ov = document.getElementById('gantt-export-overlay');
                if (ov && ov.parentNode) ov.parentNode.removeChild(ov);
              } catch (domErr2) {
                console.warn('dom-to-image retry failed:', domErr2);
                throw domErr2; // Fall through to html2canvas
              }
            } else {
              // html2canvas fallback
              // try load if missing
              await new Promise((resolve) => {
                const id = 'html2canvas-cdn';
                if (document.getElementById(id)) return resolve();
                const s = document.createElement('script');
                s.id = id; s.async = true;
                s.onload = resolve;
                s.src = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js';
                document.body.appendChild(s);
              });
              if (window.html2canvas) {
                // Calculate optimal canvas options based on container size
                const containerRect = container.getBoundingClientRect();
                const totalPixels = containerRect.width * containerRect.height;
                const maxPixels = 50000000; // 50 million pixels limit
                
                let optimalScale = isLargeDataset ? 1.5 : 2; // Increase minimum scale for better text quality
                if (totalPixels * (optimalScale * optimalScale) > maxPixels) {
                  optimalScale = Math.sqrt(maxPixels / totalPixels);
                  optimalScale = Math.max(1.0, Math.min(optimalScale, 2)); // Minimum scale 1.0 for better text clarity
                  console.log(`[Export] Adjusted scale to ${optimalScale.toFixed(2)} due to large canvas size`);
                }
                
                // Optimized html2canvas options for large datasets
                const canvasOptions = {
                  backgroundColor: '#ffffff',
                  scale: optimalScale,
                  useCORS: true,
                  logging: false,
                  allowTaint: true,
                  foreignObjectRendering: false, // Disable for better compatibility
                  imageTimeout: isLargeDataset ? 30000 : 15000, // Longer timeout for large datasets
                  removeContainer: true,
                  async: true,
                  // Add explicit size limits
                  width: Math.min(container.scrollWidth, 16383), // Half of max canvas size
                  height: Math.min(container.scrollHeight, 16383)
                };
                
                try {
                  console.log('[Export] Attempting html2canvas with options:', canvasOptions);
                  
                  // Add timeout wrapper for html2canvas
                  const canvasPromise = window.html2canvas(container, canvasOptions);
                  const timeoutPromise = new Promise((_, reject) => {
                    setTimeout(() => reject(new Error('html2canvas timeout')), canvasOptions.imageTimeout || 30000);
                  });
                  
                  let canvas = await Promise.race([canvasPromise, timeoutPromise]);
                  console.log('[Export] Canvas generated:', { width: canvas?.width, height: canvas?.height });
                  
                  if (!canvas || canvas.width === 0 || canvas.height === 0) {
                    console.warn('[Export] Invalid canvas dimensions, trying with reduced options');
                    // Fallback with minimal options
                    const fallbackOptions = {
                      backgroundColor: '#ffffff',
                      scale: 1,
                      useCORS: false,
                      logging: true,
                      allowTaint: false,
                      height: Math.min(container.scrollHeight, 32767), // Max canvas height limit
                      width: Math.min(container.scrollWidth, 32767)    // Max canvas width limit
                    };
                    const fallbackCanvas = await window.html2canvas(container, fallbackOptions);
                    if (!fallbackCanvas || fallbackCanvas.width === 0 || fallbackCanvas.height === 0) {
                      throw new Error('Canvas generation failed with fallback options');
                    }
                    // Replace the original canvas with fallback
                    canvas = fallbackCanvas;
                    console.log('[Export] Using fallback canvas:', { width: canvas.width, height: canvas.height });
                  }
                  
                  // Try different image formats and qualities - start with higher quality for better text
                  let dataUrl = null;
                  const qualities = isLargeDataset ? [0.8, 0.7, 0.6] : [1.0, 0.95, 0.8]; // Start with higher quality
                  
                  for (const quality of qualities) {
                    try {
                      dataUrl = canvas.toDataURL('image/png', quality);
                      if (dataUrl && dataUrl !== 'data:,' && dataUrl.length > 100) {
                        console.log(`[Export] Success with quality ${quality}, data URL length:`, dataUrl.length);
                        break;
                      }
                    } catch (urlErr) {
                      console.warn(`[Export] Failed to generate data URL with quality ${quality}:`, urlErr);
                    }
                  }
                  
                  // Final validation with multiple fallback strategies
                  if (!dataUrl || dataUrl === 'data:,' || dataUrl.length < 100) {
                    console.warn('[Export] PNG data URL generation failed, trying alternative methods');
                    
                    // Strategy 1: Try JPEG with different qualities - prioritize higher quality for text clarity
                    const jpegQualities = [0.95, 0.9, 0.8, 0.7]; // Higher quality range for better text
                    for (const jpegQuality of jpegQualities) {
                      try {
                        dataUrl = canvas.toDataURL('image/jpeg', jpegQuality);
                        if (dataUrl && dataUrl !== 'data:,' && dataUrl.length > 100) {
                          console.log(`[Export] JPEG fallback successful with quality ${jpegQuality}`);
                          break;
                        }
                      } catch (jpegErr) {
                        console.warn(`[Export] JPEG quality ${jpegQuality} failed:`, jpegErr);
                      }
                    }
                    
                    // Strategy 2: Try WebP if supported
                    if ((!dataUrl || dataUrl.length < 100) && canvas.toDataURL('image/webp')) {
                      try {
                        dataUrl = canvas.toDataURL('image/webp', 0.8);
                        if (dataUrl && dataUrl !== 'data:,' && dataUrl.length > 100) {
                          console.log('[Export] WebP fallback successful');
                        }
                      } catch (webpErr) {
                        console.warn('[Export] WebP fallback failed:', webpErr);
                      }
                    }
                    
                    // Strategy 3: Try canvas.toBlob() as alternative
                    if (!dataUrl || dataUrl.length < 100) {
                      try {
                        const blobPromise = new Promise((resolve, reject) => {
                          canvas.toBlob((blob) => {
                            if (blob) {
                              const reader = new FileReader();
                              reader.onload = () => resolve(reader.result);
                              reader.onerror = reject;
                              reader.readAsDataURL(blob);
                            } else {
                              reject(new Error('Blob generation failed'));
                            }
                          }, 'image/png', 0.8);
                        });
                        
                        const blobDataUrl = await blobPromise;
                        if (blobDataUrl && blobDataUrl.length > 100) {
                          dataUrl = blobDataUrl;
                          console.log('[Export] Blob method successful');
                        }
                      } catch (blobErr) {
                        console.warn('[Export] Blob method failed:', blobErr);
                      }
                    }
                    
                    // Final check
                    if (!dataUrl || dataUrl === 'data:,' || dataUrl.length < 100) {
                      throw new Error('All data URL extraction methods failed - canvas may be too large or corrupted');
                    }
                  }
                  
                  const a = document.createElement('a');
                  a.href = dataUrl;
                  
                  // Determine file extension and type based on data URL
                  let fileExtension = '.png';
                  let fileType = 'PNG';
                  if (dataUrl.startsWith('data:image/jpeg')) {
                    fileExtension = '.jpg';
                    fileType = 'JPEG';
                  } else if (dataUrl.startsWith('data:image/webp')) {
                    fileExtension = '.webp';
                    fileType = 'WebP';
                  }
                  
                  a.download = `${fname}${fileExtension}`;
                  a.click();
                  
                  try { gantt.message({ type: 'success', text: `Export ${fileType} berhasil diunduh.` }); } catch(_) {}
                  
                  // Cleanup: remove canvas reference and force garbage collection
                  canvas = null;
                  if (window.gc && isLargeDataset) {
                    try { 
                      setTimeout(() => window.gc(), 1000); // Delayed GC
                    } catch(_) {}
                  }
                  
                  // Remove overlay immediately after download
                  const ov = document.getElementById('gantt-export-overlay');
                  if (ov && ov.parentNode) ov.parentNode.removeChild(ov);
                } catch (canvasErr) {
                  console.error('html2canvas failed:', canvasErr);
                  throw new Error(`Export gagal: ${canvasErr.message}`);
                }
              } else {
                throw new Error('Tidak ada library export yang tersedia');
              }
            }
          }
          // Restore dimensions
          try {
            container.style.height = prevCont.h;
            container.style.width = prevCont.w;
            container.style.overflow = prevCont.ov;
            if (layout) { layout.style.width = prevLayout.w || ''; layout.style.overflow = prevLayout.ov || ''; }
            if (taskArea) { taskArea.style.width = prevTask.w || ''; taskArea.style.overflow = prevTask.ov || ''; }
            // Restore gantt config
            if (Array.isArray(gantt.config.columns)) {
              gantt.config.columns.forEach((col, idx) => {
                if (prevColWidths[idx] !== undefined) {
                  col.width = prevColWidths[idx];
                }
              });
            }
            gantt.config.row_height = prevRowHeight;
            gantt.config.bar_height = prevBarHeight;
            // Force garbage collection if available
            if (window.gc && isLargeDataset) {
              try { window.gc(); } catch(_) {}
            }
            gantt.render();
            // Remove export-specific style
            const sty = document.getElementById('gantt-export-style');
            if (sty && sty.parentNode) sty.parentNode.removeChild(sty);
            // Remove overlay on success as well
            const ov = document.getElementById('gantt-export-overlay');
            if (ov && ov.parentNode) ov.parentNode.removeChild(ov);
            const ovcss = document.getElementById('gantt-export-overlay-style');
            if (ovcss && ovcss.parentNode) ovcss.parentNode.removeChild(ovcss);
          } catch(_) {}
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Export image failed', e);
          const errorMsg = isLargeDataset 
            ? `Gagal export ${taskCount} task. Coba tutup aplikasi lain untuk menghemat memory.`
            : 'Gagal melakukan export gambar';
          gantt.message({ type: 'error', text: errorMsg });
          // Always remove overlay and export style in error case
          try {
            const ov = document.getElementById('gantt-export-overlay');
            if (ov && ov.parentNode) ov.parentNode.removeChild(ov);
            const ovcss = document.getElementById('gantt-export-overlay-style');
            if (ovcss && ovcss.parentNode) ovcss.parentNode.removeChild(ovcss);
            const sty = document.getElementById('gantt-export-style');
            if (sty && sty.parentNode) sty.parentNode.removeChild(sty);
          } catch(_) {}
        }
      }
    };
    onReady(api);
  }, [onReady, refresh, handleOpenAddTaskDialog]);

  // Open actual dialog and fetch data
  const openActualDialog = useCallback(async (task) => {
    setActualDialogTask(task);
    setActualDialogOpen(true);
    setActualDialogLoading(true);
    try {
      // Fetch approved submissions with attachments via hook
      const detailed = await getItemsByTask(task.id, { limit: 1000 });
      setActualDialogItems(Array.isArray(detailed) ? detailed : []);
    } catch (err) {
      console.error('Failed to fetch progress submissions', err);
      setActualDialogItems([]);
    } finally {
      setActualDialogLoading(false);
    }
  }, [getItemsByTask]);

  useEffect(() => {
    openActualDialogRef.current = openActualDialog;
  }, [openActualDialog]);

  // Re-render gantt when approved index is ready to apply the actual bar visibility
  useEffect(() => {
    try {
      if (!progressIndexLoading) {
        gantt.render();
      }
    } catch (_) {}
  }, [progressIndexLoading]);

  const closeActualDialog = useCallback(() => {
    setActualDialogOpen(false);
    setActualDialogTask(null);
    setActualDialogItems([]);
  }, []);

  return (
    <>
      <style>{`
        .gantt_grid_scale, .gantt_task_scale { height: 64px; }
        .gantt_scale_line { line-height: 20px; }
        .gantt_task_line.bar-project { background-color: #1a73e8; border-color: #1765cc; }
        .gantt_task_line.bar-modular { background-color: #34a853; border-color: #2c8c46; }
        .gantt_task_line.bar-category { background-color: #fbbc05; border-color: #d6a404; }
        .gantt_task_line.bar-task { background-color: #e53935; border-color: #c62828; }
        .gantt_task_line.bar-actual { 
          background-color: #6a1fa9; 
          border-color: #4a148c; 
          height: 16px !important;
          margin-top: 3px !important;
          margin-bottom: 3px !important;
        }
        /* Hide drag/link handles for actual rows but keep progress visible */
        .gantt_task_line.bar-actual .gantt_task_drag { display: none !important; }
        .gantt_task_line.bar-actual .gantt_task_progress_drag { display: none !important; }
        .gantt_task_line.bar-actual .gantt_link_point { display: none !important; }
        
        /* Custom lightbox styling */
        .gantt_cal_light {
          width: 600px !important;
          height: auto !important;
          left: 50% !important;
          top: 50% !important;
          transform: translate(-50%, -50%) !important;
        }
        .gantt_section_time {
          background: #f8f9fa;
          padding: 10px !important;
          border-radius: 4px;
        }
        .gantt_section_duration {
          gap: 5px !important;
        }
        /* Date picker styling */
        .date-picker-row input[type="date"],
        .date-picker-row input[type="number"] {
          font-size: 14px;
          border: 1px solid #ddd;
          border-radius: 4px;
          padding: 6px 8px;
          transition: border-color 0.2s;
        }
        .date-picker-row input[type="date"]:focus,
        .date-picker-row input[type="number"]:focus {
          outline: none;
          border-color: #2196f3;
          box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
        }
        .date-picker-row input[readonly] {
          background-color: #f5f5f5;
          cursor: default;
        }
        /* Remove custom lightbox button icons */
        .gantt_cal_ltext textarea {
          width: 100% !important;
          box-sizing: border-box;
        }
        .gantt_cal_ltext select { width: 100% !important; padding: 4px; }
        
        /* Force styles within bottom bar containers */
        .gantt_cal_light .gantt_cal_lbottom .gantt_btn_set .gantt_button {
          border: 0 !important;
          box-shadow: none !important;
          background-image: none !important;
        }
        .gantt_cal_light .gantt_cal_lbottom .gantt_button.gantt_save_btn,
        .gantt_cal_light .gantt_btn_set .gantt_left_btns .gantt_button.gantt_save_btn {
          background-color: #2e7d32 !important;
          color: #ffffff !important;
        }
        .gantt_cal_light .gantt_cal_lbottom .gantt_button.gantt_split_btn,
        .gantt_cal_light .gantt_btn_set .gantt_right_btns .gantt_button.gantt_split_btn {
          background-color: #fb8c00 !important;
          color: #ffffff !important;
        }
        .gantt_cal_light .gantt_cal_lbottom .gantt_button.gantt_save_btn *,
        .gantt_cal_light .gantt_cal_lbottom .gantt_button.gantt_split_btn * {
          color: #ffffff !important;
        }
        .glabel {
          font-size: 12px;
          color: #666;
          margin-bottom: 4px;
        }
        .gpred-row select, .gpred-row input[type="number"] {
          padding: 6px 8px;
          border: 1px solid #cfd8dc;
          border-radius: 4px;
        }

        /* (Removed custom lightbox button styles to use defaults) */
        /* Progress strip: smaller height and centered position for better visibility */
        .gantt_task_progress {
          height: 4px !important;
          bottom: 2px !important;
          border-radius: 2px;
          opacity: 0.9 !important;
          position: absolute;
          box-shadow: 0 1px 2px rgba(0,0,0,0.2);
        }
        .gantt_task_progress_drag {
          height: 4px !important;
          top: auto !important;
          bottom: 2px !important;
        }
        /* Enhanced progress colors with better contrast and visual distinction */
        .gantt_task_line.bar-task.prog-low .gantt_task_progress { 
          background: linear-gradient(90deg, #ff9800, #fb8c00) !important;
          border: 1px solid #e65100;
        }
        .gantt_task_line.bar-task.prog-mid .gantt_task_progress { 
          background: linear-gradient(90deg, #2196f3, #1e88e5) !important;
          border: 1px solid #0d47a1;
        }
        .gantt_task_line.bar-task.prog-high .gantt_task_progress { 
          background: linear-gradient(90deg, #4caf50, #2e7d32) !important;
          border: 1px solid #1b5e20;
        }
        /* Hide text on Actual bar */
        .gantt_task_line.bar-actual .gantt_task_content,
        .gantt_task_line.bar-actual .gantt_task_text { display: none !important; }
        
        /* Interactive timeline drag cursor */
        .gantt_task_bg, .gantt_task_row, .gantt_data_area {
          cursor: grab !important;
        }
        .gantt_task_bg:active, .gantt_task_row:active, .gantt_data_area:active {
          cursor: grabbing !important;
        }
        
        /* Hand cursor for timeline area */
        .gantt_timeline_area {
          cursor: grab !important;
        }
        .gantt_timeline_area:active {
          cursor: grabbing !important;
        }

        /* Actual progress bar styling - enhanced for better visibility */
        .g-actual-wrap { z-index: 1; }
        .g-actual-bar { 
          background: linear-gradient(90deg, #7b1fa2, #6a1b9a); 
          border: 1px solid #4a148c; 
          opacity: 0.9;
          height: 3px !important;
          bottom: 1px !important;
          border-radius: 1.5px;
          box-shadow: 0 1px 3px rgba(106, 27, 154, 0.4);
        }
        .g-actual-wrap:hover .g-actual-bar { 
          opacity: 1; 
          box-shadow: 0 2px 4px rgba(106, 27, 154, 0.6);
        }
        
        /* Hide progress bars for actual rows to remove yellow line */
        .gantt_task_line.bar-actual .gantt_task_progress {
          display: none !important;
        }
          
        .gantt-hidden-row {
          display: none !important;
        }
      `}</style>
      <div
        style={{
          background: 'rgba(255, 235, 59, 0.35)',
          border: '1px solid rgba(255, 235, 59, 0.8)',
          color: '#5d4037',
          padding: '8px 12px',
          marginBottom: '8px',
          borderRadius: '6px',
          fontSize: '14px'
        }}
      >
        <b>Petunjuk Penggunaan :</b>
        <br />
        - Untuk menambahkan pekerjaan baru, pilih kategori pekerjaan terlebih dahulu, lalu klik tombol "TAMBAH PEKERJAAN" di toolbar atas.<br />
        - Klik 2x pada pekerjaan (task) untuk melihat detail pekerjaan yang bersangkutan.<br />
        - Klik 2x pada panah untuk menghapus predecessors pekerjaan yang berkaitan.
      </div>
      <div style={{ width: '100%', height: `${height}px` }} ref={ganttRef} />
      <ActualProgressDialog
        open={actualDialogOpen}
        onClose={closeActualDialog}
        task={actualDialogTask}
        items={actualDialogItems}
        loading={actualDialogLoading}
      />
      
      {/* Add Task Dialog */}
      <AddTaskDialog
        open={addTaskDialogOpen}
        onClose={handleCloseAddTaskDialog}
        onSave={handleSaveNewTask}
        loading={addTaskLoading}
        categoryData={selectedCategoryForNewTask}
        projectId={projectId}
      />

      {/* Task Split Dialog */}
      {taskToSplit && (
        <TaskSplitDialog
          open={splitDialogOpen}
          onClose={handleCloseSplitDialog}
          task={taskToSplit}
          onSplit={handleTaskSplit}
        />
      )}
    </>
  );
}
